// src/components/Admin/FirmPortalAIGovernance.js
// AI Governance Dashboard - Audit trail and model usage tracking for SOC 2 compliance
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Grid, CircularProgress, Alert, Chip, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select,
    MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Card, CardContent, IconButton, Tooltip, TablePagination,
    Link, Collapse
} from '@mui/material';
import {
    SmartToy as AIIcon,
    CheckCircle as SuccessIcon,
    Warning as FallbackIcon,
    Category as ModelsIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    OpenInNew as OpenInNewIcon,
    FilterList as FilterIcon,
    Share as ShareIcon,
    // P2: Review tracking icons
    RateReview as ReviewIcon,
    Pending as PendingIcon,
    ThumbUp as ApprovedIcon,
    ThumbDown as RejectedIcon,
    Security as DataProtectionIcon,
    Person as PersonIcon,
    // P5: Expandable row icons
    KeyboardArrowDown as ExpandMoreIcon,
    KeyboardArrowUp as ExpandLessIcon
} from '@mui/icons-material';
import { db, functions } from '../../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import ShareModal from '../ShareModal';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, Cell, PieChart, Pie, Legend
} from 'recharts';
import { toast } from 'react-toastify';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType } from 'docx';

// Provider colors for charts
const PROVIDER_COLORS = {
    'Anthropic Claude Sonnet 4.5': '#6366f1',
    'Anthropic Claude Opus 4.5': '#8b5cf6',
    'OpenAI GPT-4o': '#10b981',
    'OpenAI GPT-5.1': '#059669',
    'Perplexity Sonar Pro': '#f59e0b',
    'Perplexity Sonar': '#d97706',
    'Unknown': '#9ca3af'
};

const DEFAULT_COLOR = '#6b7280';

// Human-readable labels for governance dataPath values
const DATA_PATH_LABELS = {
    'questionnaire': 'CEO Questionnaire',
    'Q&A': 'Q&A',
    'strategicPulse': 'Strategic Pulse',
    'financialTextExtraction': 'Financial Documents',
    'quickbooksFinancialAnalysis': 'QuickBooks Analysis',
    'decisionMemo': 'Decision Memo',
    'executiveSummary': 'Executive Summary',
    'industryInsights': 'Industry Insights',
    'savvaiSignals_stage2': 'Signal DLP Scan',
    'savvaiSignals_stage3': 'Signal DLP Scan',
    'savvaiSignals_alert': 'Signal DLP Scan',
    'savvaiSignals_digest': 'Signal DLP Scan',
    'clientProfile': 'Client Profile',
    'masterSummaryUpdate': 'Master Summary',
    'chatSummaryGeneration': 'Chat Summary',
    'icrGeneration': 'Company Research',
    'documentUpload': 'Document Upload',
    'documentDelete': 'Document Delete',
    'documentPin': 'Document Pin',
    'documentUnpin': 'Document Unpin',
    'financialIntelligence': 'Financial Intelligence',
};
const FI_SITUATION_LABELS = {
  prepare_for_meeting: 'Pre-meeting brief', board_package: 'Board Package',
  understand_finances: 'Financial Health Diagnostic', decision_support: 'Decision Support',
  cash_flow_brief: 'Cash Flow Brief', cash_flow_deep_dive: 'Cash Flow Deep Dive',
  investor_readiness: 'Investor Readiness', forecast_intelligence: 'Forecast Intelligence',
  margin_pricing: 'Margin & Pricing', variance_brief: 'Variance Brief',
};
const formatDataPath = (path) => DATA_PATH_LABELS[path] || path;

const GOVERNANCE_ONLY_PATHS = new Set([
    'strategicPulse',
    'questionnaire',
    'financialTextExtraction',
    'quickbooksFinancialAnalysis',
    'clientProfile',
    'masterSummaryUpdate',
    'chatSummaryGeneration',
    'icrGeneration',
    'executiveSummary',
    'industryInsights',
    'savvaiSignals_stage2',
    'savvaiSignals_stage3',
    'savvaiSignals_alert',
    'savvaiSignals_digest',
    'documentUpload',
    'documentDelete',
    'documentPin',
    'documentUnpin',
    'financialIntelligence',
]);

// Helper to get date range for period
const getDateRangeForPeriod = (period) => {
    const now = new Date();
    let start, end;
    
    switch (period) {
        case 'MTD':
            start = new Date(now.getFullYear(), now.getMonth(), 1);
            end = now;
            break;
        case 'QTD':
            const quarterStart = Math.floor(now.getMonth() / 3) * 3;
            start = new Date(now.getFullYear(), quarterStart, 1);
            end = now;
            break;
        case 'YTD':
            start = new Date(now.getFullYear(), 0, 1);
            end = now;
            break;
        case 'ALL':
        default:
            start = new Date(2020, 0, 1); // Far back enough to capture all data
            end = now;
            break;
    }
    
    return { start, end };
};

// Helper to safely get createdAt date
const getCreatedAt = (item) => {
    if (!item?.createdAt) return null;
    if (typeof item.createdAt.toDate === 'function') {
        return item.createdAt.toDate();
    }
    if (item.createdAt instanceof Date) {
        return item.createdAt;
    }
    return new Date(item.createdAt);
};

const FirmPortalAIGovernance = ({ user, orgData, orgConfig }) => {
    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeView, setActiveView] = useState('summary'); // 'summary' or 'audit'
    
    // Date range state - default to last 30 days (matching Analytics pattern)
    const getDefaultDateFrom = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };
    const getDefaultDateTo = () => {
        return new Date().toISOString().split('T')[0];
    };
    const [dateFrom, setDateFrom] = useState(getDefaultDateFrom);
    const [dateTo, setDateTo] = useState(getDefaultDateTo);
    
    // Data state
    const [allItems, setAllItems] = useState([]); // Combined Q&A, Signals, Decision Memos
    const [clients, setClients] = useState([]);
    
        // Global client scope + audit trail filters
    const [clientFilter, setClientFilter] = useState('all');
    const [contentTypeFilter, setContentTypeFilter] = useState('all');
    const [providerFilter, setProviderFilter] = useState('all');
    const [fallbackFilter, setFallbackFilter] = useState('all');
    const [reviewStatusFilter, setReviewStatusFilter] = useState('all'); // P2: Review filter
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const clientOptions = useMemo(() => {
        return Object.entries(clients)
            .map(([id, data]) => ({ id, ...data }))
            .sort((a, b) =>
                ((a.name || a.displayName || '')).localeCompare(b.name || b.displayName || '')
            );
    }, [clients]);

    const selectedClientName = useMemo(() => {
        if (clientFilter === 'all') return 'All Clients';
        return clients[clientFilter]?.name || clients[clientFilter]?.displayName || 'Selected Client';
    }, [clientFilter, clients]);
    
    // Export state
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState('csv');
    const [isExporting, setIsExporting] = useState(false);
    
    // Share state
    const [shareModalOpen, setShareModalOpen] = useState(false);
    
    // P5: Expanded rows state for audit trail
    const [expandedRows, setExpandedRows] = useState(new Set());
  
    // Access log state
    const [accessLogs, setAccessLogs] = useState([]);
    const [accessLogsLoading, setAccessLogsLoading] = useState(false);

    // QW2/3: Governance events and review queue state
    const [governanceEvents, setGovernanceEvents] = useState([]);
    const [reviewQueuePending, setReviewQueuePending] = useState(0);
    const [reviewQueueItems, setReviewQueueItems] = useState([]);

    // Phase C #5: Review Compliance state (firm portal aggregate view)
    const [complianceData, setComplianceData] = useState(null);
    const [complianceLoading, setComplianceLoading] = useState(false);
    const [complianceError, setComplianceError] = useState(null);
    const [expandedFcfoRows, setExpandedFcfoRows] = useState(new Set());
    const [reviewCertExportFormat, setReviewCertExportFormat] = useState('pdf');
    const [certExportingId, setCertExportingId] = useState(null);

    // Extracted fetch function so Refresh button can call it
    const fetchGovernanceData = async () => {
        if (!user?.organizationId) {
            setError("No organization ID found.");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Fetch questions (Q&A items)
            const questionsQuery = query(
                collection(db, 'questions'),
                where('organizationId', '==', user.organizationId),
                orderBy('createdAt', 'desc')
            );
            const questionsSnap = await getDocs(questionsQuery);
            
            // Fetch businessUpdates (SavvaiSignals)
            const updatesQuery = query(
                collection(db, 'businessUpdates'),
                where('organizationId', '==', user.organizationId),
                orderBy('createdAt', 'desc')
            );
            const updatesSnap = await getDocs(updatesQuery);

            // Fetch clients for name lookup
            const clientsQuery = query(
                collection(db, 'clients'),
                where('organizationId', '==', user.organizationId)
            );
            const clientsSnap = await getDocs(clientsQuery);
            const clientMap = {};
            clientsSnap.docs.forEach(doc => {
                clientMap[doc.id] = doc.data();
            });
            setClients(clientMap);

            // Process questions
            const items = [];
            questionsSnap.docs.forEach(doc => {
                const data = doc.data();
                
               // Add the main Q&A item if it has governance data
                if (data.provider || data.aiAuditMetadata) {
                    items.push({
                        id: doc.id,
                        type: 'qa',
                        typeLabel: 'Q&A',
                        clientId: data.clientId,
                        clientName: clientMap[data.clientId]?.name || clientMap[data.clientId]?.displayName || 'Unknown',
                        provider: data.provider || 'Unknown',
                        aiAuditMetadata: data.aiAuditMetadata || null,
                        governanceResult: data.governanceResult || null,
                        wasFallback: data.aiAuditMetadata?.synthesisWasFallback || false,
                        createdAt: data.createdAt,
                        questionPreview: data.questionText?.substring(0, 80) || 'No question text',
                        // P2: Review tracking fields
                        reviewStatus: data.reviewStatus || null,
                        reviewedByEmail: data.reviewedByEmail || null,
                        reviewedAt: data.reviewedAt || null,
                        reviewHistory: data.reviewHistory || []
                    });
                }
                
                // Check followUps for Decision Memos
                if (data.followUps && Array.isArray(data.followUps)) {
                    data.followUps.forEach((followUp, idx) => {
                        if (followUp.type === 'decision_memo' && (followUp.provider || followUp.aiAuditMetadata)) {
                            items.push({
                                id: `${doc.id}_followup_${idx}`,
                                parentId: doc.id,
                                type: 'decision_memo',
                                typeLabel: 'Decision Memo',
                                clientId: data.clientId,
                                clientName: clientMap[data.clientId]?.name || clientMap[data.clientId]?.displayName || 'Unknown',
                                provider: followUp.provider || 'Unknown',
                                aiAuditMetadata: followUp.aiAuditMetadata || null,
                                wasFallback: followUp.aiAuditMetadata?.synthesisWasFallback || false,
                                createdAt: followUp.createdAt || data.createdAt,
                                questionPreview: followUp.questionText || followUp.title || followUp.decision || 'Decision Memo',
                                // P2: Review tracking fields
                                reviewStatus: followUp.reviewStatus || null,
                                reviewedByEmail: followUp.reviewedByEmail || null,
                                reviewedAt: followUp.reviewedAt || null,
                                reviewHistory: followUp.reviewHistory || [],
                                governanceResult: followUp.governanceResult || null
                            });
                        }
                    });
                }
            });

            // Process businessUpdates (SavvaiSignals)
            updatesSnap.docs.forEach(doc => {
                const data = doc.data();
                
                if (data.provider || data.aiAuditMetadata) {
                    items.push({
                        id: doc.id,
                        type: 'signal',
                        typeLabel: 'SavvaiSignal',
                        clientId: data.clientId,
                        clientName: clientMap[data.clientId]?.name || clientMap[data.clientId]?.displayName || 'Unknown',
                        provider: data.provider || 'Unknown',
                        aiAuditMetadata: data.aiAuditMetadata || null,
                        governanceResult: data.governanceResult || null,
                        wasFallback: data.aiAuditMetadata?.synthesisWasFallback || false,
                        createdAt: data.createdAt,
                        questionPreview: data.headline || data.title || 'SavvaiSignal',
                        // P2: Review tracking fields
                        reviewStatus: data.reviewStatus || null,
                        reviewedByEmail: data.reviewedByEmail || null,
                        reviewedAt: data.reviewedAt || null,
                        reviewHistory: data.reviewHistory || []
                    });
                }
                
                // Check followUps for Decision Memos in signals too
                if (data.followUps && Array.isArray(data.followUps)) {
                    data.followUps.forEach((followUp, idx) => {
                        if (followUp.type === 'decision_memo' && (followUp.provider || followUp.aiAuditMetadata)) {
                            items.push({
                                id: `${doc.id}_followup_${idx}`,
                                parentId: doc.id,
                                type: 'decision_memo',
                                typeLabel: 'Decision Memo',
                                clientId: data.clientId,
                                clientName: clientMap[data.clientId]?.name || clientMap[data.clientId]?.displayName || 'Unknown',
                                provider: followUp.provider || 'Unknown',
                                aiAuditMetadata: followUp.aiAuditMetadata || null,
                                wasFallback: followUp.aiAuditMetadata?.synthesisWasFallback || false,
                                createdAt: followUp.createdAt || data.createdAt,
                                questionPreview: followUp.questionText || followUp.title || followUp.decision || 'Decision Memo',
                                // P2: Review tracking fields
                                reviewStatus: followUp.reviewStatus || null,
                                reviewedByEmail: followUp.reviewedByEmail || null,
                                reviewedAt: followUp.reviewedAt || null,
                                reviewHistory: followUp.reviewHistory || [],
                                governanceResult: followUp.governanceResult || null
                            });
                        }
                    });
                }
            });

            // QW2: Fetch governance events by organizationId (covers ALL data paths)
// Note: Events written before this fix lack organizationId and won't appear here.
// They remain visible in individual user portal dashboards via userId query.
try {
    const govQuery = query(
        collection(db, 'governanceEvents'),
        where('organizationId', '==', user.organizationId)
    );
    const govSnap = await getDocs(govQuery);
    const govEvents = govSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    govEvents.sort((a, b) => {
        const tsA = a.timestamp?.toDate?.() || (a.timestamp ? new Date(a.timestamp) : new Date(0));
        const tsB = b.timestamp?.toDate?.() || (b.timestamp ? new Date(b.timestamp) : new Date(0));
        return tsB - tsA;
    });
    setGovernanceEvents(govEvents);

    const govOnlyItems = govEvents
        .filter(e => GOVERNANCE_ONLY_PATHS.has(e.dataPath))
        .map(e => ({
            id: `gov_${e.id}`,
            type: e.dataPath === 'financialIntelligence' ? 'financial_intelligence' : 'governance_event',
            typeLabel: DATA_PATH_LABELS[e.dataPath] || e.dataPath,
            clientId: e.clientId === 'multi_client' ? null : e.clientId,
            clientName: e.clientId === 'multi_client'
                ? 'Multiple Clients'
                : clientMap[e.clientId]?.name || clientMap[e.clientId]?.displayName || 'Unknown Client',
            provider: 'Governance Event',
            aiAuditMetadata: null,
            governanceResult: {
                governed: true,
                method: e.method || 'unknown',
                riskLevel: e.riskLevel || 'CLEAR',
                totalPiiFound: e.totalPiiFound || 0,
                action: e.action || 'PROCEED',
                governanceEventId: e.id,
            },
            wasFallback: false,
            createdAt: e.timestamp,
            questionPreview: e.metadata?.originalFileName
                ? `${DATA_PATH_LABELS[e.dataPath] || e.dataPath} — ${e.metadata.originalFileName}`
                : e.metadata?.source
                    ? `${DATA_PATH_LABELS[e.dataPath] || e.dataPath} — ${e.metadata.source}`
                    : (DATA_PATH_LABELS[e.dataPath] || e.dataPath),
            reviewStatus: null,
            reviewedByEmail: null,
            reviewedAt: null,
            reviewHistory: [],
            isGovernanceOnly: true,
            dataPath: e.dataPath,
        }));

        // Fetch FI subcollection entries (reviewable artifacts — separate from governance events above)
    const fiItems = [];
    try {
        for (const [clientId, clientData] of Object.entries(clientMap)) {
            const fiQuery = query(
                collection(db, 'clients', clientId, 'financialIntelligence'),
                where('status', '==', 'complete'),
                orderBy('createdAt', 'desc')
            );
            const fiSnap = await getDocs(fiQuery);
            fiSnap.docs.forEach(fiDoc => {
                const fi = fiDoc.data();
                fiItems.push({
                    id: fiDoc.id,
                    type: 'financial_intelligence',
                    typeLabel: 'Financial Intelligence',
                    clientId,
                    clientName: clientData.name || clientData.displayName || 'Unknown Client',
                    provider: fi.provider || 'Unknown',
                    aiAuditMetadata: fi.aiAuditMetadata || null,
                    governanceResult: fi.governanceEventId
                        ? { governed: true, governanceEventId: fi.governanceEventId }
                        : null,
                    wasFallback: false,
                    createdAt: fi.createdAt,
                    questionPreview: (fi.triggerType === 'situation_starter' && FI_SITUATION_LABELS[fi.triggerLabel])
                    ? FI_SITUATION_LABELS[fi.triggerLabel]
                    : (fi.triggerLabel || fi.rawPrompt?.substring(0, 120) || 'Financial Intelligence'),
                    reviewStatus: fi.reviewStatus || null,
                    reviewedByEmail: fi.reviewedByEmail || null,
                    reviewedAt: fi.reviewedAt || null,
                    reviewNotes: fi.reviewNotes || null,
                    reviewHistory: fi.reviewHistory || [],
                    governanceEventId: fi.governanceEventId || null,
                    analysisLenses: fi.analysisLenses || [],
                    dataSnapshot: fi.dataSnapshot || null,
                });
            });
        }
    } catch (fiErr) {
        console.error('[Governance] Error fetching FI entries:', fiErr);
    }

    const combinedItems = [...items, ...govOnlyItems, ...fiItems];
    combinedItems.sort((a, b) => {
        const dateA = getCreatedAt(a);
        const dateB = getCreatedAt(b);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB - dateA;
    });

    setAllItems(combinedItems);
} catch (govErr) {
    console.error('Error fetching governance events:', govErr);
    setGovernanceEvents([]);
    items.sort((a, b) => {
        const dateA = getCreatedAt(a);
        const dateB = getCreatedAt(b);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB - dateA;
    });
    setAllItems(items);
}

            // QW3: Fetch governance review queue items by organizationId (full data for exports)
            try {
                const reviewQuery = query(
                    collection(db, 'governanceReviewQueue'),
                    where('organizationId', '==', user.organizationId)
                );
                const reviewSnap = await getDocs(reviewQuery);
                const items = reviewSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                items.sort((a, b) => {
                    const tsA = a.createdAt?.toDate?.() || (a.createdAt ? new Date(a.createdAt) : new Date(0));
                    const tsB = b.createdAt?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : new Date(0));
                    return tsB - tsA;
                });
                setReviewQueueItems(items);
                setReviewQueuePending(items.filter(i => i.status === 'pending').length);
            } catch (reviewErr) {
                console.error('Error fetching review queue:', reviewErr);
                setReviewQueueItems([]);
                setReviewQueuePending(0);
            }

            setLoading(false);

        } catch (err) {
            console.error('Error fetching governance data:', err);
            setError(`Failed to load governance data: ${err.message}`);
            setLoading(false);
        }
    };

    // Fetch on mount and when org changes
    useEffect(() => {
        fetchGovernanceData();
    }, [user?.organizationId]);

    // Extracted so export/share handlers can refresh after writing
    const fetchAccessLogs = async () => {
        if (!user?.organizationId) return;
        
        try {
            setAccessLogsLoading(true);
            const logsQuery = query(
                collection(db, 'auditLogs'),
                where('organizationId', '==', user.organizationId),
                orderBy('timestamp', 'desc')
            );
            const logsSnap = await getDocs(logsQuery);
            
            const logs = logsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            setAccessLogs(logs);
        } catch (err) {
            console.error('Error fetching access logs:', err);
        } finally {
            setAccessLogsLoading(false);
        }
    };

    useEffect(() => {
        fetchAccessLogs();
    }, [user?.organizationId]);

    // Phase C #5: Fetch review compliance data (FCFOs, review logs, incidents)
    const fetchReviewCompliance = async () => {
        if (!user?.organizationId) return;
        setComplianceLoading(true);
        setComplianceError(null);

        try {
            // 1. Get all FCFOs in this organization
            const usersQuery = query(
                collection(db, 'users'),
                where('organizationId', '==', user.organizationId)
            );
            const usersSnap = await getDocs(usersQuery);
            const fcfos = usersSnap.docs.map(doc => ({
                uid: doc.id,
                email: doc.data().email || 'Unknown',
                firstName: doc.data().firstName || '',
                lastName: doc.data().lastName || '',
                displayName: doc.data().displayName || doc.data().email || 'Unknown',
                active: doc.data().active !== false,
            }));

            // 2. Get all review logs for this organization
            const logsQuery = query(
                collection(db, 'governanceReviewLogs'),
                where('organizationId', '==', user.organizationId)
            );
            const logsSnap = await getDocs(logsQuery);
            const reviewLogs = logsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));

            // 3. Get all incidents for this organization
            const incidentsQuery = query(
                collection(db, 'governanceIncidents'),
                where('organizationId', '==', user.organizationId)
            );
            const incidentsSnap = await getDocs(incidentsQuery);
            const incidents = incidentsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            incidents.sort((a, b) => {
                const tsA = a.createdAt?.toDate?.() || (a.createdAt ? new Date(a.createdAt) : new Date(0));
                const tsB = b.createdAt?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : new Date(0));
                return tsB - tsA;
            });

            // 4. Build last 6 months period list
            const months = [];
            const now = new Date();
            for (let i = 1; i <= 6; i++) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                months.push({ year: d.getFullYear(), month: d.getMonth() });
            }

            // 5. Map review logs by reviewer + period key
            const logsByKey = {};
            reviewLogs.forEach(log => {
                const ps = log.periodStart?.toDate?.() || (log.periodStart ? new Date(log.periodStart) : null);
                if (!ps) return;
                const key = `${log.reviewerId}_${ps.getUTCFullYear()}-${ps.getUTCMonth()}`;
                logsByKey[key] = log;
            });

            // 6. Map incidents by reviewer
            const incidentsByReviewer = {};
            incidents.forEach(inc => {
                const reviewLog = reviewLogs.find(rl => rl.id === inc.reviewLogId);
                const uid = reviewLog?.reviewerId || null;
                if (uid) {
                    if (!incidentsByReviewer[uid]) incidentsByReviewer[uid] = [];
                    incidentsByReviewer[uid].push(inc);
                }
            });

            // 7. Build per-FCFO compliance rows
            const fcfoRows = fcfos.map(fcfo => {
                const monthStatuses = months.map(m => {
                    const key = `${fcfo.uid}_${m.year}-${m.month}`;
                    const log = logsByKey[key];
                    if (log) {
                        return {
                            year: m.year,
                            month: m.month,
                            status: 'complete',
                            assessment: log.overallAssessment,
                            autoGenerated: log.autoGenerated || false,
                            reviewLogId: log.id,
                        };
                    }
                    return { year: m.year, month: m.month, status: 'missing' };
                });

                const completedCount = monthStatuses.filter(ms => ms.status === 'complete').length;
                const fcfoIncidents = incidentsByReviewer[fcfo.uid] || [];
                const fcfoLogs = reviewLogs.filter(rl => rl.reviewerId === fcfo.uid)
                    .sort((a, b) => {
                        const tsA = a.createdAt?.toDate?.() || new Date(0);
                        const tsB = b.createdAt?.toDate?.() || new Date(0);
                        return tsB - tsA;
                    });

                return {
                    ...fcfo,
                    monthStatuses,
                    completedCount,
                    totalMonths: months.length,
                    completionRate: months.length > 0 ? Math.round((completedCount / months.length) * 100) : 0,
                    incidentCount: fcfoIncidents.length,
                    openIncidentCount: fcfoIncidents.filter(i => i.status === 'reported').length,
                    reviewLogs: fcfoLogs,
                };
            });

            // Sort: lowest completion rate first (most attention needed)
            fcfoRows.sort((a, b) => a.completionRate - b.completionRate);

            // 8. Compute aggregate metrics
            const totalFcfos = fcfoRows.length;
            const currentMonth = months[0];
            const currentMonthComplete = fcfoRows.filter(f =>
                f.monthStatuses[0]?.status === 'complete'
            ).length;
            const overallCompletionRate = totalFcfos > 0
                ? Math.round((currentMonthComplete / totalFcfos) * 100) : 0;
            const openIncidents = incidents.filter(i => i.status === 'reported').length;

            setComplianceData({
                fcfoRows,
                incidents,
                months,
                totalFcfos,
                currentMonthComplete,
                overallCompletionRate,
                openIncidents,
                totalIncidents: incidents.length,
            });
        } catch (err) {
            console.error('[ReviewCompliance] Error fetching compliance data:', err);
            setComplianceError(err.message);
        } finally {
            setComplianceLoading(false);
        }
    };

        // Filter items by date range + selected client
    const filteredByPeriod = useMemo(() => {
        const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
        const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
        
        return allItems.filter(item => {
            const createdAt = getCreatedAt(item);
            if (!createdAt) return false;
            if (clientFilter !== 'all' && item.clientId !== clientFilter) return false;
            if (fromDate && createdAt < fromDate) return false;
            if (toDate && createdAt > toDate) return false;
            return true;
        });
    }, [allItems, dateFrom, dateTo, clientFilter]);

    // Calculate summary metrics
    const summaryMetrics = useMemo(() => {
        const total = filteredByPeriod.length;
        const fallbacks = filteredByPeriod.filter(item => item.wasFallback).length;
        const primarySuccess = total > 0 ? ((total - fallbacks) / total * 100).toFixed(1) : 0;
        
        // Count unique providers (both synthesis and research)
    const providers = new Set();
    filteredByPeriod.forEach(item => {
        if (item.provider && item.provider !== 'Unknown') {
            providers.add(item.provider);
        }
        // Also count research model
        const researchModel = item.aiAuditMetadata?.researchModel;
        if (researchModel) {
            const researchLabel = researchModel === 'sonar-pro' ? 'Perplexity Sonar Pro' 
                : researchModel === 'sonar' ? 'Perplexity Sonar'
                : researchModel;
            providers.add(researchLabel);
        }
    });
        
        return {
            total,
            fallbacks,
            primarySuccessRate: primarySuccess,
            activeModels: providers.size
        };
    }, [filteredByPeriod]);

    // P2: Calculate review metrics by content type
    const reviewMetrics = useMemo(() => {
            const byType = {
            qa: { total: 0, reviewed: 0, approved: 0, rejected: 0, draft: 0 },
            signal: { total: 0, reviewed: 0, approved: 0, rejected: 0, draft: 0 },
            decision_memo: { total: 0, reviewed: 0, approved: 0, rejected: 0, draft: 0 },
            financial_intelligence: { total: 0, reviewed: 0, approved: 0, rejected: 0, draft: 0 }
        };
        
        filteredByPeriod.forEach(item => {
            if (byType[item.type]) {
                byType[item.type].total++;
                if (item.reviewStatus) {
                    byType[item.type].reviewed++;
                    if (item.reviewStatus === 'approved') byType[item.type].approved++;
                    if (item.reviewStatus === 'rejected') byType[item.type].rejected++;
                    if (item.reviewStatus === 'draft') byType[item.type].draft++;
                }
            }
        });
        
        const totalItems = filteredByPeriod.filter(i => !i.isGovernanceOnly).length;
        const totalReviewed = filteredByPeriod.filter(i => !i.isGovernanceOnly && i.reviewStatus).length;
        const totalApproved = filteredByPeriod.filter(i => !i.isGovernanceOnly && i.reviewStatus === 'approved').length;
        
        return {
            byType,
            overall: {
                total: totalItems,
                reviewed: totalReviewed,
                approved: totalApproved,
                reviewRate: totalItems > 0 ? ((totalReviewed / totalItems) * 100).toFixed(1) : 0,
                approvalRate: totalReviewed > 0 ? ((totalApproved / totalReviewed) * 100).toFixed(1) : 0
            }
        };
    }, [filteredByPeriod]);

    // P3: Calculate anonymization/data protection metrics (supports BOTH old anonymization + new governance schemas)
const anonymizationMetrics = useMemo(() => {
  let totalWithProof = 0;
  let totalBackfilled = 0;
  let totalWithDetailedMetrics = 0;
  let totalGovernedByDLP = 0;
  let totalGovernedByRegex = 0;
  
  // PII aggregates (only from non-backfilled docs)
  let piiPatternsTotal = 0;
  let emailsRemoved = 0;
  let phonesRemoved = 0;
  let ssnsRemoved = 0;
  let addressesRemoved = 0;
  let companyNamesRemoved = 0;
  
  filteredByPeriod.forEach(item => {
    const anonDetails = item.aiAuditMetadata?.anonymizationDetails;
    const anonApplied = item.aiAuditMetadata?.anonymizationApplied;
    const govResult = item.governanceResult;
    const govSummary = item.aiAuditMetadata?.governanceSummary;
    
    // Check BOTH old anonymization schema AND new governance schema
    const hasProof = anonApplied || govResult?.governed;
    
    if (hasProof) {
      totalWithProof++;
      
      if (govResult?.governed) {
        // New governance schema (Items 7+): enforceGovernance() pipeline
        totalWithDetailedMetrics++;
        piiPatternsTotal += govResult.totalPiiFound || govSummary?.totalPiiFound || 0;
        if (govResult.method === 'dlp' || govSummary?.method === 'dlp') {
          totalGovernedByDLP++;
        } else {
          totalGovernedByRegex++;
        }
      } else if (anonDetails?.backfilled) {
        totalBackfilled++;
      } else if (anonDetails) {
        // Old anonymization schema (pre-Item 7)
        totalWithDetailedMetrics++;
        piiPatternsTotal += anonDetails.piiPatternsDetected || 0;
        emailsRemoved += anonDetails.piiPatternsRemoved?.emails || 0;
        phonesRemoved += anonDetails.piiPatternsRemoved?.phones || 0;
        ssnsRemoved += anonDetails.piiPatternsRemoved?.ssns || 0;
        addressesRemoved += anonDetails.piiPatternsRemoved?.addresses || 0;
        if (anonDetails.companyNameRemoved) companyNamesRemoved++;
      }
    }
  });
  
  const total = filteredByPeriod.length;
  const complianceRate = total > 0 ? ((totalWithProof / total) * 100).toFixed(1) : 0;
  
  return {
    total,
    totalWithProof,
    totalBackfilled,
    totalWithDetailedMetrics,
    totalGovernedByDLP,
    totalGovernedByRegex,
    complianceRate,
    piiPatternsTotal,
    breakdown: {
      emails: emailsRemoved,
      phones: phonesRemoved,
      ssns: ssnsRemoved,
      addresses: addressesRemoved,
      companyNames: companyNamesRemoved
    }
  };
}, [filteredByPeriod]);

    // Filter governance events by date range + selected client
        const filteredGovernanceEvents = useMemo(() => {
        const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
        const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;

        return governanceEvents.filter(event => {
            const ts = event.timestamp?.toDate?.() || (event.timestamp ? new Date(event.timestamp) : null);
            if (!ts) return false;
            if (clientFilter !== 'all' && event.clientId !== clientFilter) return false;
            if (fromDate && ts < fromDate) return false;
            if (toDate && ts > toDate) return false;
            return true;
        });
    }, [governanceEvents, dateFrom, dateTo, clientFilter]);

    const filteredReviewQueueItems = useMemo(() => {
        const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
        const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;

        return reviewQueueItems.filter(item => {
            const createdAt = item.createdAt?.toDate?.() || (item.createdAt ? new Date(item.createdAt) : null);
            if (!createdAt) return false;
            if (clientFilter !== 'all' && item.clientId !== clientFilter) return false;
            if (fromDate && createdAt < fromDate) return false;
            if (toDate && createdAt > toDate) return false;
            return true;
        });
    }, [reviewQueueItems, dateFrom, dateTo, clientFilter]);

    // QW2: Calculate DLP governance pipeline metrics from governanceEvents collection
    const governanceMetrics = useMemo(() => {
        const total = filteredGovernanceEvents.length;
        const riskCounts = { CLEAR: 0, LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
        const methodCounts = { dlp: 0, regex: 0 };
        const actionCounts = { PROCEED: 0, REVIEW: 0, BLOCK: 0 };
        const dataPathCounts = {};
        const piiTypeCounts = {};
        
        filteredGovernanceEvents.forEach(event => {
            if (riskCounts[event.riskLevel] !== undefined) riskCounts[event.riskLevel]++;
            if (event.method === 'dlp') methodCounts.dlp++;
            else if (event.method === 'regex') methodCounts.regex++;
            if (actionCounts[event.action] !== undefined) actionCounts[event.action]++;
            const dp = event.dataPath || 'unknown';
            dataPathCounts[dp] = (dataPathCounts[dp] || 0) + 1;
            if (event.dlpFindings && Array.isArray(event.dlpFindings)) {
                event.dlpFindings.forEach(finding => {
                    const type = finding.infoType || finding.type || 'UNKNOWN';
                    piiTypeCounts[type] = (piiTypeCounts[type] || 0) + (finding.count || 1);
                });
            }
        });
        
        const dlpRate = total > 0 ? ((methodCounts.dlp / total) * 100).toFixed(1) : '0';
        const cleanRate = total > 0 ? (((riskCounts.CLEAR + riskCounts.LOW) / total) * 100).toFixed(1) : '0';
        
        return { total, riskCounts, methodCounts, actionCounts, dataPathCounts, piiTypeCounts, dlpRate, cleanRate };
    }, [filteredGovernanceEvents]);

    // Provider breakdown for chart - tracks BOTH synthesis and research models
    const providerBreakdown = useMemo(() => {
        const synthesisCounts = {};
        const researchCounts = {};
        
        filteredByPeriod.forEach(item => {
            // Count synthesis model (Claude/OpenAI)
            const synthesisProvider = item.provider || 'Unknown';
            synthesisCounts[synthesisProvider] = (synthesisCounts[synthesisProvider] || 0) + 1;
            
            // Count research model (Perplexity) if present
            const researchModel = item.aiAuditMetadata?.researchModel;
            if (researchModel) {
                const researchLabel = researchModel === 'sonar-pro' ? 'Perplexity Sonar Pro' 
                    : researchModel === 'sonar' ? 'Perplexity Sonar'
                    : researchModel;
                researchCounts[researchLabel] = (researchCounts[researchLabel] || 0) + 1;
            }
        });
        
        // Combine both into a single breakdown with role indicators
        const combined = [];
        
        // Add synthesis models
        Object.entries(synthesisCounts).forEach(([name, value]) => {
            combined.push({
                name: name,
                value,
                role: 'Synthesis',
                percentage: filteredByPeriod.length > 0 
                    ? ((value / filteredByPeriod.length) * 100).toFixed(1)
                    : 0
            });
        });
        
        // Add research models
        Object.entries(researchCounts).forEach(([name, value]) => {
            combined.push({
                name: name,
                value,
                role: 'Research',
                percentage: filteredByPeriod.length > 0 
                    ? ((value / filteredByPeriod.length) * 100).toFixed(1)
                    : 0
            });
        });
        
        return combined.sort((a, b) => b.value - a.value);
    }, [filteredByPeriod]);

    // Separate lists for filters
    const uniqueSynthesisProviders = useMemo(() => {
        const providers = new Set();
        allItems.forEach(item => {
            if (item.provider && !item.isGovernanceOnly) providers.add(item.provider);
        });
        return Array.from(providers).sort();
    }, [allItems]);

    const uniqueResearchProviders = useMemo(() => {
        const providers = new Set();
        allItems.forEach(item => {
            const researchModel = item.aiAuditMetadata?.researchModel;
            if (researchModel) {
                const label = researchModel === 'sonar-pro' ? 'Perplexity Sonar Pro' 
                    : researchModel === 'sonar' ? 'Perplexity Sonar'
                    : researchModel;
                providers.add(label);
            }
        });
        return Array.from(providers).sort();
    }, [allItems]);

    // Content type breakdown
    const contentTypeBreakdown = useMemo(() => {
        const counts = { qa: 0, signal: 0, decision_memo: 0 };
        filteredByPeriod.forEach(item => {
            if (counts[item.type] !== undefined) {
                counts[item.type]++;
            }
        });
        return [
            { name: 'Q&A', value: counts.qa },
            { name: 'SavvaiSignals', value: counts.signal },
            { name: 'Decision Memos', value: counts.decision_memo }
        ].filter(item => item.value > 0);
    }, [filteredByPeriod]);

    // Apply audit trail filters
    const filteredAuditItems = useMemo(() => {
        return filteredByPeriod.filter(item => {
            if (contentTypeFilter !== 'all' && item.type !== contentTypeFilter) return false;
            // Provider filter - handle both synthesis and research models
            if (providerFilter !== 'all') {
                if (providerFilter.startsWith('research:')) {
                    // Filter by research model
                    const researchModel = item.aiAuditMetadata?.researchModel;
                    const researchLabel = researchModel === 'sonar-pro' ? 'Perplexity Sonar Pro' 
                        : researchModel === 'sonar' ? 'Perplexity Sonar'
                        : researchModel;
                    if (researchLabel !== providerFilter.replace('research:', '')) return false;
                } else {
                    // Filter by synthesis model
                    if (item.provider !== providerFilter) return false;
                }
            }
            if (fallbackFilter === 'yes' && !item.wasFallback) return false;
            if (fallbackFilter === 'no' && item.wasFallback) return false;
            // P2: Review status filter
            if (reviewStatusFilter === 'pending' && (item.reviewStatus || item.isGovernanceOnly)) return false;
            if (reviewStatusFilter === 'approved' && item.reviewStatus !== 'approved') return false;
            if (reviewStatusFilter === 'rejected' && item.reviewStatus !== 'rejected') return false;
            if (reviewStatusFilter === 'draft' && item.reviewStatus !== 'draft') return false;
            if (reviewStatusFilter === 'reviewed' && !item.reviewStatus) return false;
            return true;
        });
    }, [filteredByPeriod, contentTypeFilter, providerFilter, fallbackFilter, reviewStatusFilter]);

    // Get unique providers for filter dropdown
    const uniqueProviders = useMemo(() => {
        const providers = new Set();
        allItems.forEach(item => {
            if (item.provider) providers.add(item.provider);
        });
        return Array.from(providers).sort();
    }, [allItems]);

    // P5: Toggle row expansion
    const toggleRowExpand = (itemId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    // Format date for display
    const formatDate = (item) => {
        const date = getCreatedAt(item);
        if (!date) return 'N/A';
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format access log timestamp
    const formatAccessLogDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = typeof timestamp.toDate === 'function' 
            ? timestamp.toDate() 
            : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Export functions
    const generateCSV = () => {
        const rows = [];
        rows.push(['AI Governance Audit Trail Export']);
        rows.push([`Organization: ${orgData?.name || 'N/A'}`]);
        rows.push([`Client Scope: ${selectedClientName}`]);
        rows.push([`Date Range: ${dateFrom} to ${dateTo}`]);
        rows.push([`Export Date: ${new Date().toLocaleString()}`]);
        rows.push([]);
        
        // Summary
        rows.push(['=== SUMMARY ===']);
        rows.push(['Total AI-Generated Items', summaryMetrics.total]);
        rows.push(['Primary Model Success Rate', `${summaryMetrics.primarySuccessRate}%`]);
        rows.push(['Fallback Events', summaryMetrics.fallbacks]);
        rows.push(['Active Models', summaryMetrics.activeModels]);
        rows.push([]);
        
        // P2: Review Compliance
        rows.push(['=== HUMAN REVIEW COMPLIANCE ===']);
        rows.push(['Overall Review Rate', `${reviewMetrics.overall.reviewRate}%`]);
        rows.push(['Items Reviewed', reviewMetrics.overall.reviewed]);
        rows.push(['Items Pending Review', reviewMetrics.overall.total - reviewMetrics.overall.reviewed]);
        rows.push(['Items Approved', reviewMetrics.overall.approved]);
        rows.push(['Approval Rate (of reviewed)', `${reviewMetrics.overall.approvalRate}%`]);
        rows.push([]);
        rows.push(['Review by Content Type', 'Total', 'Reviewed', 'Review Rate']);
        rows.push(['Q&A', reviewMetrics.byType.qa.total, reviewMetrics.byType.qa.reviewed, 
            reviewMetrics.byType.qa.total > 0 ? `${((reviewMetrics.byType.qa.reviewed / reviewMetrics.byType.qa.total) * 100).toFixed(1)}%` : 'N/A']);
        rows.push(['SavvaiSignals', reviewMetrics.byType.signal.total, reviewMetrics.byType.signal.reviewed,
            reviewMetrics.byType.signal.total > 0 ? `${((reviewMetrics.byType.signal.reviewed / reviewMetrics.byType.signal.total) * 100).toFixed(1)}%` : 'N/A']);
                rows.push(['Decision Memos', reviewMetrics.byType.decision_memo.total, reviewMetrics.byType.decision_memo.reviewed,
            reviewMetrics.byType.decision_memo.total > 0 ? `${((reviewMetrics.byType.decision_memo.reviewed / reviewMetrics.byType.decision_memo.total) * 100).toFixed(1)}%` : 'N/A']);
        rows.push(['Financial Intelligence', reviewMetrics.byType.financial_intelligence.total, reviewMetrics.byType.financial_intelligence.reviewed,
            reviewMetrics.byType.financial_intelligence.total > 0 ? `${((reviewMetrics.byType.financial_intelligence.reviewed / reviewMetrics.byType.financial_intelligence.total) * 100).toFixed(1)}%` : 'N/A']);
        rows.push([]);

// P4: Data Protection Compliance
rows.push(['=== DATA PROTECTION COMPLIANCE ===']);
rows.push(['Compliance Rate', `${anonymizationMetrics.complianceRate}%`]);
rows.push(['Documents with Anonymization Proof', `${anonymizationMetrics.totalWithProof} of ${anonymizationMetrics.total}`]);
rows.push(['Documents with Detailed Metrics', anonymizationMetrics.totalWithDetailedMetrics]);
rows.push(['Documents Backfilled (Legacy)', anonymizationMetrics.totalBackfilled]);
rows.push(['Total PII Patterns Scrubbed', anonymizationMetrics.piiPatternsTotal]);
rows.push([]);
rows.push(['PII Breakdown', 'Count']);
rows.push(['Company Names Scrubbed', anonymizationMetrics.breakdown.companyNames]);
rows.push(['Email Addresses Scrubbed', anonymizationMetrics.breakdown.emails]);
rows.push(['Phone Numbers Scrubbed', anonymizationMetrics.breakdown.phones]);
rows.push(['Street Addresses Scrubbed', anonymizationMetrics.breakdown.addresses]);
rows.push(['SSNs Scrubbed', anonymizationMetrics.breakdown.ssns]);
rows.push([]);

        rows.push(['=== DLP GOVERNANCE PIPELINE ===']);
rows.push(['Total Governance Events', filteredGovernanceEvents.length]);
rows.push(['DLP Method', filteredGovernanceEvents.filter(e => e.method === 'dlp').length]);
rows.push(['Regex Fallback', filteredGovernanceEvents.filter(e => e.method !== 'dlp').length]);
rows.push(['Data Paths Covered', [...new Set(filteredGovernanceEvents.map(e => e.dataPath))].map(p => formatDataPath(p)).join(', ')]);
const csvRiskCounts = {};
filteredGovernanceEvents.forEach(e => { csvRiskCounts[e.riskLevel] = (csvRiskCounts[e.riskLevel] || 0) + 1; });
rows.push(['Risk Distribution', Object.entries(csvRiskCounts).map(([level, count]) => `${level}: ${count}`).join(', ')]);
rows.push([]);
rows.push(['Governance Event Log']);
rows.push(['Timestamp', 'Data Path', 'Risk Level', 'Action', 'Method', 'PII Found', 'PII Types']);
filteredGovernanceEvents.slice(0, 200).forEach(event => {
    const ts = event.timestamp?.toDate?.()
        ? event.timestamp.toDate().toLocaleString()
        : event.timestamp ? new Date(event.timestamp).toLocaleString() : '';
    const piiTypes = (event.dlpFindings || []).map(f => `${f.infoType}:${f.count}`).join('; ');
    rows.push([ts, formatDataPath(event.dataPath), event.riskLevel || '', event.action || '', event.method === 'dlp' ? 'DLP' : 'Regex', event.totalPiiFound || 0, piiTypes]);
});
if (filteredReviewQueueItems.length > 0) {
    rows.push([]);
    rows.push(['Governance Review Queue']);
    rows.push(['Date', 'Data Path', 'Risk Level', 'Status', 'Reviewer', 'Notes']);
    filteredReviewQueueItems.forEach(item => {
        const ts = item.createdAt?.toDate?.()
            ? item.createdAt.toDate().toLocaleString()
            : item.createdAt ? new Date(item.createdAt).toLocaleString() : '';
        rows.push([ts, formatDataPath(item.dataPath), item.riskLevel || '', item.status || '', item.reviewedBy || '', `"${(item.reviewNotes || '').replace(/"/g, '""').replace(/[\r\n]+/g, ' ')}"`]);
    });
}
rows.push([]);

        // Provider breakdown
        rows.push(['=== PROVIDER BREAKDOWN ===']);
        rows.push(['Provider', 'Count', 'Percentage']);
        providerBreakdown.forEach(p => {
            rows.push([p.name, p.value, `${p.percentage}%`]);
        });
        rows.push([]);
        
        // Audit trail
        rows.push(['=== AUDIT TRAIL ===']);
                rows.push(['Date', 'Content Type', 'Client', 'Provider', 'Fallback', 'Review Status', 'Reviewed By', '# Reviews', 'Model ID', 'Research Model', 'Description']);
        filteredAuditItems.forEach(item => {
            rows.push([
                formatDate(item),
                item.typeLabel,
                item.clientName,
                item.provider,
                item.wasFallback ? 'Yes' : 'No',
                item.isGovernanceOnly ? 'Auto-Governed' : (item.reviewStatus || 'Pending'),
                item.reviewedByEmail || 'N/A',
                item.reviewHistory?.length || 0,
                item.aiAuditMetadata?.synthesisModel || 'N/A',
                item.aiAuditMetadata?.researchModel || 'N/A',
                `"${(item.questionPreview || '').replace(/"/g, '""').replace(/[\r\n]+/g, ' ').replace(/—/g, '-')}"`
            ]);
        });
        
        // P5B: Add detailed review history section
        const itemsWithHistory = filteredAuditItems.filter(item => item.reviewHistory && item.reviewHistory.length > 0);
        if (itemsWithHistory.length > 0) {
            rows.push([]);
            rows.push(['=== REVIEW HISTORY DETAIL ===']);
            rows.push(['Artifact Date', 'Artifact Type', 'Client', 'Review Date', 'Action', 'Previous Status', 'New Status', 'Notes', 'Reviewer']);
            
            itemsWithHistory.forEach(item => {
                const artifactDate = formatDate(item);
                const artifactType = item.typeLabel;
                const clientName = item.clientName;
                
                // Add each history entry as a row
                [...item.reviewHistory].reverse().forEach(entry => {
                    const reviewDate = entry.timestamp?.toDate 
                        ? entry.timestamp.toDate().toLocaleString()
                        : entry.timestamp 
                            ? new Date(entry.timestamp).toLocaleString()
                            : 'N/A';
                    
                    const actionLabel = entry.action === 'status_set' ? 'Initial Review'
                        : entry.action === 'status_changed' ? 'Status Changed'
                        : entry.action === 'clarification_added' ? 'Clarification Added'
                        : entry.action || 'Unknown';
                    
                    rows.push([
                        artifactDate,
                        artifactType,
                        clientName,
                        reviewDate,
                        actionLabel,
                        entry.previousStatus || 'N/A',
                        entry.newStatus || 'N/A',
                        `"${(entry.notes || '').replace(/"/g, '""').replace(/[\r\n]+/g, ' ')}"`,
                        entry.userEmail || 'N/A'
                    ]);
                });
            });
        }
        
        return rows.map(row => 
            row.map(cell => {
                const str = (cell?.toString() || '').replace(/"/g, '""').replace(/[\r\n]+/g, ' ').replace(/—/g, '-');
                return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str}"` : str;
            }).join(',')
        ).join('\n');
    };

    const generateWordDocument = async () => {
        const children = [];
        
        children.push(new Paragraph({ 
            text: "AI Governance Report", 
            heading: HeadingLevel.TITLE, 
            spacing: { after: 200 } 
        }));
        children.push(new Paragraph({ 
            children: [
                new TextRun({ text: "Organization: ", bold: true }), 
                new TextRun({ text: orgData?.name || 'N/A' })
            ], 
            spacing: { after: 100 } 
        }));
                children.push(new Paragraph({ 
            children: [
                new TextRun({ text: "Client Scope: ", bold: true }), 
                new TextRun({ text: selectedClientName })
            ], 
            spacing: { after: 100 } 
        }));
        children.push(new Paragraph({ 
            children: [
                new TextRun({ text: "Date Range: ", bold: true }), 
                new TextRun({ text: `${dateFrom} to ${dateTo}` })
            ], 
            spacing: { after: 100 } 
        }));
        children.push(new Paragraph({ 
            children: [
                new TextRun({ text: "Export Date: ", bold: true }), 
                new TextRun({ text: new Date().toLocaleString() })
            ], 
            spacing: { after: 300 } 
        }));
        
        // Summary section
        children.push(new Paragraph({ 
            text: "Summary Metrics", 
            heading: HeadingLevel.HEADING_1, 
            spacing: { before: 200, after: 150 } 
        }));
        
        const summaryTable = new DocxTable({
            rows: [
                new DocxTableRow({ children: [
                    new DocxTableCell({ children: [new Paragraph({ text: "Metric" })], shading: { fill: 'E8E8E8' } }),
                    new DocxTableCell({ children: [new Paragraph({ text: "Value" })], shading: { fill: 'E8E8E8' } })
                ]}),
                new DocxTableRow({ children: [
                    new DocxTableCell({ children: [new Paragraph({ text: "Total AI-Generated Items" })] }), 
                    new DocxTableCell({ children: [new Paragraph({ text: summaryMetrics.total.toString() })] })
                ]}),
                new DocxTableRow({ children: [
                    new DocxTableCell({ children: [new Paragraph({ text: "Primary Model Success Rate" })] }), 
                    new DocxTableCell({ children: [new Paragraph({ text: `${summaryMetrics.primarySuccessRate}%` })] })
                ]}),
                new DocxTableRow({ children: [
                    new DocxTableCell({ children: [new Paragraph({ text: "Fallback Events" })] }), 
                    new DocxTableCell({ children: [new Paragraph({ text: summaryMetrics.fallbacks.toString() })] })
                ]}),
                new DocxTableRow({ children: [
                    new DocxTableCell({ children: [new Paragraph({ text: "Active Models" })] }), 
                    new DocxTableCell({ children: [new Paragraph({ text: summaryMetrics.activeModels.toString() })] })
                ]})
            ],
            width: { size: 100, type: WidthType.PERCENTAGE }
        });
        children.push(summaryTable);
        
        // Provider breakdown
        children.push(new Paragraph({ 
            text: "Provider Breakdown", 
            heading: HeadingLevel.HEADING_1, 
            spacing: { before: 400, after: 150 } 
        }));
        
        const providerRows = [
            new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: "Provider" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Count" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Percentage" })], shading: { fill: 'E8E8E8' } })
            ]})
        ];
        providerBreakdown.forEach(p => {
            providerRows.push(new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: p.name })] }),
                new DocxTableCell({ children: [new Paragraph({ text: p.value.toString() })] }),
                new DocxTableCell({ children: [new Paragraph({ text: `${p.percentage}%` })] })
            ]}));
        });
        children.push(new DocxTable({ rows: providerRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

// P4: Data Protection Compliance
children.push(new Paragraph({
  text: "Data Protection Compliance",
  heading: HeadingLevel.HEADING_1,
  spacing: { before: 400, after: 150 }
}));

const dataProtectionRows = [
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Metric" })], shading: { fill: 'E8E8E8' } }),
    new DocxTableCell({ children: [new Paragraph({ text: "Value" })], shading: { fill: 'E8E8E8' } })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Compliance Rate" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: `${anonymizationMetrics.complianceRate}%` })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Documents with Anonymization Proof" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: `${anonymizationMetrics.totalWithProof} of ${anonymizationMetrics.total}` })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Documents with Detailed Metrics" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.totalWithDetailedMetrics.toString() })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Documents Backfilled (Legacy)" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.totalBackfilled.toString() })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Total PII Patterns Scrubbed" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.piiPatternsTotal.toString() })] })
  ]})
];
children.push(new DocxTable({ rows: dataProtectionRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

// PII Breakdown sub-table
children.push(new Paragraph({
  children: [new TextRun({ text: "PII Removal Breakdown", bold: true })],
  spacing: { before: 200, after: 100 }
}));

const piiBreakdownRows = [
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "PII Type" })], shading: { fill: 'E8E8E8' } }),
    new DocxTableCell({ children: [new Paragraph({ text: "Count Removed" })], shading: { fill: 'E8E8E8' } })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Company Names" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.breakdown.companyNames.toString() })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Email Addresses" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.breakdown.emails.toString() })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Phone Numbers" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.breakdown.phones.toString() })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "Street Addresses" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.breakdown.addresses.toString() })] })
  ]}),
  new DocxTableRow({ children: [
    new DocxTableCell({ children: [new Paragraph({ text: "SSNs" })] }),
    new DocxTableCell({ children: [new Paragraph({ text: anonymizationMetrics.breakdown.ssns.toString() })] })
  ]})
];
children.push(new DocxTable({ rows: piiBreakdownRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

// DLP Governance Pipeline Events
children.push(new Paragraph({
    text: "DLP Governance Pipeline",
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 400, after: 100 }
}));
children.push(new Paragraph({
    children: [new TextRun({
        text: "Every AI interaction passes through the DLP governance pipeline before client data reaches any external AI provider. " +
              "This section documents each governance scan, what was detected, and the enforcement action taken.",
        italics: true,
        color: '555555'
    })],
    spacing: { after: 150 }
}));

if (filteredGovernanceEvents.length === 0) {
    children.push(new Paragraph({
        text: "No governance events recorded in this date range.",
        spacing: { after: 200 }
    }));
} else {
    const govSummaryRows = [
        new DocxTableRow({ children: [
            new DocxTableCell({ children: [new Paragraph({ text: "Metric" })], shading: { fill: 'E8E8E8' } }),
            new DocxTableCell({ children: [new Paragraph({ text: "Value" })], shading: { fill: 'E8E8E8' } })
        ]}),
                    new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: "Total Governance Events" })] }),
                new DocxTableCell({ children: [new Paragraph({ text: filteredGovernanceEvents.length.toString() })] })
            ]}),
            new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: "DLP Method" })] }),
                new DocxTableCell({ children: [new Paragraph({ text: filteredGovernanceEvents.filter(e => e.method === 'dlp').length.toString() })] })
            ]}),
            new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: "Regex Fallback" })] }),
                new DocxTableCell({ children: [new Paragraph({ text: filteredGovernanceEvents.filter(e => e.method !== 'dlp').length.toString() })] })
            ]}),
            new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: "Data Paths Covered" })] }),
                new DocxTableCell({ children: [new Paragraph({ text: [...new Set(filteredGovernanceEvents.map(e => e.dataPath))].map(p => formatDataPath(p)).join(', ') })] })
            ]})
        ];
        const wordRiskCounts = {};
        filteredGovernanceEvents.forEach(e => { wordRiskCounts[e.riskLevel] = (wordRiskCounts[e.riskLevel] || 0) + 1; });
    govSummaryRows.push(new DocxTableRow({ children: [
        new DocxTableCell({ children: [new Paragraph({ text: "Risk Distribution" })] }),
        new DocxTableCell({ children: [new Paragraph({ text: Object.entries(wordRiskCounts).map(([level, count]) => `${level}: ${count}`).join(', ') })] })
    ]}));
    children.push(new DocxTable({ rows: govSummaryRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

    children.push(new Paragraph({
        children: [new TextRun({ text: "Governance Event Log", bold: true })],
        spacing: { before: 200, after: 100 }
    }));
    const govEventRows = [
        new DocxTableRow({ children: [
            new DocxTableCell({ children: [new Paragraph({ text: "Timestamp" })], shading: { fill: 'E8E8E8' } }),
            new DocxTableCell({ children: [new Paragraph({ text: "Data Path" })], shading: { fill: 'E8E8E8' } }),
            new DocxTableCell({ children: [new Paragraph({ text: "Risk" })], shading: { fill: 'E8E8E8' } }),
            new DocxTableCell({ children: [new Paragraph({ text: "Action" })], shading: { fill: 'E8E8E8' } }),
            new DocxTableCell({ children: [new Paragraph({ text: "Method" })], shading: { fill: 'E8E8E8' } }),
            new DocxTableCell({ children: [new Paragraph({ text: "PII Found" })], shading: { fill: 'E8E8E8' } })
        ]})
    ];
        filteredGovernanceEvents.slice(0, 100).forEach(event => {
        const ts = event.timestamp?.toDate?.()
            ? event.timestamp.toDate().toLocaleString()
            : event.timestamp ? new Date(event.timestamp).toLocaleString() : '—';
        govEventRows.push(new DocxTableRow({ children: [
            new DocxTableCell({ children: [new Paragraph({ text: ts })] }),
            new DocxTableCell({ children: [new Paragraph({ text: formatDataPath(event.dataPath) })] }),
            new DocxTableCell({ children: [new Paragraph({ text: event.riskLevel || '—' })] }),
            new DocxTableCell({ children: [new Paragraph({ text: event.action || '—' })] }),
            new DocxTableCell({ children: [new Paragraph({ text: event.method === 'dlp' ? 'DLP' : 'Regex' })] }),
            new DocxTableCell({ children: [new Paragraph({ text: (event.totalPiiFound || 0).toString() })] })
        ]}));
    });
    children.push(new DocxTable({ rows: govEventRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

    const eventsWithPii = filteredGovernanceEvents.filter(e => e.dlpFindings && e.dlpFindings.length > 0).slice(0, 50);
    if (eventsWithPii.length > 0) {
        children.push(new Paragraph({
            children: [new TextRun({ text: "PII Detection Detail", bold: true })],
            spacing: { before: 200, after: 100 }
        }));
        eventsWithPii.forEach(event => {
            const ts = event.timestamp?.toDate?.()
                ? event.timestamp.toDate().toLocaleString()
                : event.timestamp ? new Date(event.timestamp).toLocaleString() : '—';
            const findings = event.dlpFindings.map(f => `${f.infoType}: ${f.count}`).join(', ');
            children.push(new Paragraph({
                children: [
                    new TextRun({ text: `${ts} — `, bold: true }),
                    new TextRun({ text: `${formatDataPath(event.dataPath)}: ` }),
                    new TextRun({ text: findings }),
                    new TextRun({ text: ` → ${event.action}`, bold: true })
                ],
                spacing: { after: 50 }
            }));
        });
    }

    if (filteredReviewQueueItems.length > 0) {
        children.push(new Paragraph({
            children: [new TextRun({ text: "Governance Review Queue Outcomes", bold: true })],
            spacing: { before: 200, after: 100 }
        }));
        const reviewRows = [
            new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: "Date" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Data Path" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Risk" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Status" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Reviewer" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Notes" })], shading: { fill: 'E8E8E8' } })
            ]})
        ];
        reviewQueueItems.forEach(item => {
            const ts = item.createdAt?.toDate?.()
                ? item.createdAt.toDate().toLocaleString()
                : item.createdAt ? new Date(item.createdAt).toLocaleString() : '—';
            reviewRows.push(new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: ts })] }),
                new DocxTableCell({ children: [new Paragraph({ text: formatDataPath(item.dataPath) })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.riskLevel || '—' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.status || '—' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.reviewedBy || '—' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.reviewNotes || '—' })] })
            ]}));
        });
        children.push(new DocxTable({ rows: reviewRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
    }
}

        // Audit trail (first 100 items to keep doc size reasonable)
        children.push(new Paragraph({ 
            text: "Audit Trail (Recent Items)", 
            heading: HeadingLevel.HEADING_1, 
            spacing: { before: 400, after: 150 } 
        }));
        
        const auditRows = [
            new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: "Date" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Type" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Provider" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Review" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "Reviewer" })], shading: { fill: 'E8E8E8' } }),
                new DocxTableCell({ children: [new Paragraph({ text: "# Reviews" })], shading: { fill: 'E8E8E8' } })
            ]})
        ];
                filteredAuditItems.slice(0, 100).forEach(item => {
            auditRows.push(new DocxTableRow({ children: [
                new DocxTableCell({ children: [new Paragraph({ text: formatDate(item) })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.typeLabel })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.provider })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.isGovernanceOnly ? 'Auto-Governed' : (item.reviewStatus || 'Pending') })] }),
                new DocxTableCell({ children: [new Paragraph({ text: item.reviewedByEmail || '—' })] }),
                new DocxTableCell({ children: [new Paragraph({ text: (item.reviewHistory?.length || 0).toString() })] })
            ]}));
        });
        children.push(new DocxTable({ rows: auditRows, width: { size: 100, type: WidthType.PERCENTAGE } }));

        // P5B: Add detailed review history section
        const itemsWithHistory = filteredAuditItems.filter(item => item.reviewHistory && item.reviewHistory.length > 0).slice(0, 50);
        if (itemsWithHistory.length > 0) {
            children.push(new Paragraph({ 
                text: "Review History Detail", 
                heading: HeadingLevel.HEADING_1, 
                spacing: { before: 400, after: 150 } 
            }));

            itemsWithHistory.forEach(item => {
                // Artifact header
                children.push(new Paragraph({ 
                    children: [
                        new TextRun({ text: `${item.typeLabel}: `, bold: true }),
                        new TextRun({ text: item.clientName }),
                        new TextRun({ text: ` (${formatDate(item)})` })
                    ],
                    spacing: { before: 200, after: 50 }
                }));

                // History entries
                [...item.reviewHistory].reverse().forEach(entry => {
                    const reviewDate = entry.timestamp?.toDate 
                        ? entry.timestamp.toDate().toLocaleString()
                        : entry.timestamp 
                            ? new Date(entry.timestamp).toLocaleString()
                            : 'N/A';
                    
                    const actionLabel = entry.action === 'status_set' ? '● Initial Review'
                        : entry.action === 'status_changed' ? '↻ Status Changed'
                        : entry.action === 'clarification_added' ? '✎ Clarification'
                        : entry.action || 'Unknown';
                    
                    const statusText = entry.newStatus 
                        ? (entry.previousStatus ? `${entry.previousStatus} → ${entry.newStatus}` : entry.newStatus)
                        : '';

                    children.push(new Paragraph({ 
                        children: [
                            new TextRun({ text: `  ${reviewDate} — ` }),
                            new TextRun({ text: actionLabel, bold: true }),
                            new TextRun({ text: statusText ? `: ${statusText}` : '' })
                        ],
                        spacing: { after: 25 }
                    }));

                    if (entry.notes) {
                        children.push(new Paragraph({ 
                            children: [
                                new TextRun({ text: `    "${entry.notes}"`, italics: true })
                            ],
                            spacing: { after: 25 }
                        }));
                    }

                    if (entry.userEmail) {
                        children.push(new Paragraph({ 
                            children: [
                                new TextRun({ text: `    by ${entry.userEmail}`, color: '666666' })
                            ],
                            spacing: { after: 50 }
                        }));
                    }
                });
            });
        }
        
        return await Packer.toBlob(new Document({ sections: [{ children }] }));
    };

    // Generate JSON for GRC tools (Purview, ServiceNow, etc.)
    const generateJSON = () => {
                const exportData = {
            metadata: {
                reportType: 'AI Governance Audit Trail',
                organization: orgData?.name || 'N/A',
                organizationId: user?.organizationId || 'N/A',
                exportedBy: user?.email || 'Unknown',
                clientScope: {
                    clientId: clientFilter === 'all' ? null : clientFilter,
                    clientName: selectedClientName
                },
                dateRange: {
                    from: dateFrom,
                    to: dateTo
                },
                exportedAt: new Date().toISOString(),
                version: '1.0'
            },
            summary: {
                totalItems: summaryMetrics.total,
                primaryModelSuccessRate: parseFloat(summaryMetrics.primarySuccessRate),
                fallbackEvents: summaryMetrics.fallbacks,
                activeModels: summaryMetrics.activeModels
            },
            humanReviewCompliance: {
                overall: {
                    totalItems: reviewMetrics.overall.total,
                    itemsReviewed: reviewMetrics.overall.reviewed,
                    itemsPending: reviewMetrics.overall.total - reviewMetrics.overall.reviewed,
                    itemsApproved: reviewMetrics.overall.approved,
                    reviewRate: parseFloat(reviewMetrics.overall.reviewRate),
                    approvalRate: parseFloat(reviewMetrics.overall.approvalRate)
                },
                byContentType: {
                    qa: {
                        total: reviewMetrics.byType.qa.total,
                        reviewed: reviewMetrics.byType.qa.reviewed,
                        approved: reviewMetrics.byType.qa.approved,
                        rejected: reviewMetrics.byType.qa.rejected,
                        draft: reviewMetrics.byType.qa.draft,
                        reviewRate: reviewMetrics.byType.qa.total > 0 
                            ? parseFloat(((reviewMetrics.byType.qa.reviewed / reviewMetrics.byType.qa.total) * 100).toFixed(1))
                            : 0
                    },
                    savvaiSignals: {
                        total: reviewMetrics.byType.signal.total,
                        reviewed: reviewMetrics.byType.signal.reviewed,
                        approved: reviewMetrics.byType.signal.approved,
                        rejected: reviewMetrics.byType.signal.rejected,
                        draft: reviewMetrics.byType.signal.draft,
                        reviewRate: reviewMetrics.byType.signal.total > 0 
                            ? parseFloat(((reviewMetrics.byType.signal.reviewed / reviewMetrics.byType.signal.total) * 100).toFixed(1))
                            : 0
                    },
                    decisionMemos: {
                        total: reviewMetrics.byType.decision_memo.total,
                        reviewed: reviewMetrics.byType.decision_memo.reviewed,
                        approved: reviewMetrics.byType.decision_memo.approved,
                        rejected: reviewMetrics.byType.decision_memo.rejected,
                        draft: reviewMetrics.byType.decision_memo.draft,
                        reviewRate: reviewMetrics.byType.decision_memo.total > 0 
                            ? parseFloat(((reviewMetrics.byType.decision_memo.reviewed / reviewMetrics.byType.decision_memo.total) * 100).toFixed(1))
                            : 0
}
}
},
dataProtectionCompliance: {
  complianceRate: parseFloat(anonymizationMetrics.complianceRate),
  totalDocuments: anonymizationMetrics.total,
  documentsWithProof: anonymizationMetrics.totalWithProof,
  documentsWithDetailedMetrics: anonymizationMetrics.totalWithDetailedMetrics,
  documentsBackfilled: anonymizationMetrics.totalBackfilled,
  piiPatternsTotal: anonymizationMetrics.piiPatternsTotal,
  piiBreakdown: {
    companyNames: anonymizationMetrics.breakdown.companyNames,
    emails: anonymizationMetrics.breakdown.emails,
    phones: anonymizationMetrics.breakdown.phones,
    addresses: anonymizationMetrics.breakdown.addresses,
    ssns: anonymizationMetrics.breakdown.ssns
  }
},
            dlpGovernancePipeline: {
                totalEvents: filteredGovernanceEvents.length,
                dlpMethod: filteredGovernanceEvents.filter(e => e.method === 'dlp').length,
                regexFallback: filteredGovernanceEvents.filter(e => e.method !== 'dlp').length,
                dataPathsCovered: [...new Set(filteredGovernanceEvents.map(e => e.dataPath))].map(p => formatDataPath(p)),
                riskDistribution: (() => {
                    const counts = {};
                    filteredGovernanceEvents.forEach(e => { counts[e.riskLevel] = (counts[e.riskLevel] || 0) + 1; });
                    return counts;
                })(),
                events: filteredGovernanceEvents.map(event => ({
                    timestamp: event.timestamp?.toDate?.()
                        ? event.timestamp.toDate().toISOString()
                        : event.timestamp ? new Date(event.timestamp).toISOString() : null,
                    dataPath: event.dataPath,
                    riskLevel: event.riskLevel,
                    action: event.action,
                    method: event.method,
                    totalPiiFound: event.totalPiiFound || 0,
                    dlpFindings: (event.dlpFindings || []).map(f => ({
                        infoType: f.infoType,
                        count: f.count
                    })),
                    reviewOutcome: event.reviewOutcome || null
                })),
                    reviewQueue: filteredReviewQueueItems.map(item => ({
                    createdAt: item.createdAt?.toDate?.()
                        ? item.createdAt.toDate().toISOString()
                        : item.createdAt ? new Date(item.createdAt).toISOString() : null,
                    dataPath: item.dataPath,
                    riskLevel: item.riskLevel,
                    status: item.status,
                    reviewedBy: item.reviewedBy || null,
                    reviewNotes: item.reviewNotes || null
                }))
            },
            providerUsage: providerBreakdown.map(p => ({
                provider: p.name,
                role: p.role,
                count: p.value,
                percentage: parseFloat(p.percentage)
            })),
            contentTypeBreakdown: contentTypeBreakdown.map(c => ({
                type: c.name,
                count: c.value
            })),
            auditTrail: filteredAuditItems.map(item => ({
                id: item.id,
                parentId: item.parentId || null,
                contentType: item.type,
                contentTypeLabel: item.typeLabel,
                clientId: item.clientId,
                clientName: item.clientName,
                createdAt: getCreatedAt(item)?.toISOString() || null,
                aiGeneration: {
                    provider: item.provider,
                    synthesisModel: item.aiAuditMetadata?.synthesisModel || null,
                    synthesisModelLabel: item.aiAuditMetadata?.synthesisModelLabel || null,
                    researchModel: item.aiAuditMetadata?.researchModel || null,
                    wasFallback: item.wasFallback,
                    inputSources: item.aiAuditMetadata?.inputSources || null
                },
                humanReview: {
                    status: item.isGovernanceOnly ? 'auto_governed' : (item.reviewStatus || 'pending'),
                    reviewedBy: item.reviewedByEmail || null,
                    reviewedAt: item.reviewedAt 
                        ? (typeof item.reviewedAt.toDate === 'function' 
                            ? item.reviewedAt.toDate().toISOString() 
                            : new Date(item.reviewedAt).toISOString())
                        : null,
                    historyCount: item.reviewHistory?.length || 0,
                    // P5B: Add full review history
                    history: (item.reviewHistory || []).map(entry => ({
                        action: entry.action || null,
                        previousStatus: entry.previousStatus || null,
                        newStatus: entry.newStatus || null,
                        notes: entry.notes || null,
                        userId: entry.userId || null,
                        userEmail: entry.userEmail || null,
                        timestamp: entry.timestamp?.toDate 
                            ? entry.timestamp.toDate().toISOString()
                            : entry.timestamp 
                                ? new Date(entry.timestamp).toISOString()
                                : null
                    }))
                },
                description: item.questionPreview || null
            }))
        };

        return JSON.stringify(exportData, null, 2);
    };

        const handleExportReviewCertificate = async (log, format) => {
        if (!log?.id) {
            toast.error('No review log available to export.');
            return;
        }

        setCertExportingId(log.id);
        try {
            const periodStartDate = log.periodStart?.toDate
                ? log.periodStart.toDate()
                : new Date(log.periodStart);
            const periodEndDate = log.periodEnd?.toDate
                ? log.periodEnd.toDate()
                : new Date(log.periodEnd);
            const periodLabel = `${periodStartDate.toLocaleDateString('en-US', { timeZone: 'UTC' })} - ${periodEndDate.toLocaleDateString('en-US', { timeZone: 'UTC' })}`;
            const filenameBase = `Governance-Review-Certificate-${periodStartDate.toISOString().split('T')[0]}-to-${periodEndDate.toISOString().split('T')[0]}`;

            let blob;
            let filename;

            if (format === 'pdf') {
                const generatePDFFn = httpsCallable(functions, 'generateGovernancePDF');
                const result = await generatePDFFn({
                    reportType: 'reviewCertificate',
                    reviewLogId: log.id
                });

                if (!result.data?.success || !result.data?.pdfBase64) {
                    throw new Error(result.data?.error || 'PDF generation failed');
                }

                const byteCharacters = atob(result.data.pdfBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                blob = new Blob([byteArray], { type: 'application/pdf' });
                filename = result.data?.filename || `${filenameBase}.pdf`;

            } else if (format === 'csv') {
                const assessmentLabel = log.overallAssessment === 'controls_effective'
                    ? 'Controls Effective'
                    : 'Finding Identified';
                const rows = [
                    ['AI Governance Review Certificate'],
                    ['Advisor', log.reviewerEmail || 'Unknown'],
                    ['Review Period', periodLabel],
                    ['Generated', new Date().toLocaleString()],
                    [],
                    ['Assessment', assessmentLabel],
                    ['Total Events in Period', log.totalEventsInPeriod || 0],
                    ['HIGH / CRITICAL Events', log.highCriticalInPeriod || 0],
                    ['Events Sampled', log.sampleSize || 0],
                    ['Items Confirmed Clean', log.itemsConfirmedClean || 0],
                    ['Findings Count', log.findingsCount || 0],
                    ['Review Type', log.autoGenerated ? 'Auto-Generated' : 'Manual Review'],
                    ['Review Log ID', log.id],
                    ['Completed At', log.createdAt?.toDate ? log.createdAt.toDate().toLocaleString() : log.createdAt || '—'],
                    ...(log.findingsDetails ? [['Finding Details', log.findingsDetails]] : []),
                ];
                const csv = rows
                    .map(row => row.map(cell => `"${(cell ?? '').toString().replace(/"/g, '""')}"`).join(','))
                    .join('\n');
                blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                filename = `${filenameBase}.csv`;

            } else if (format === 'json') {
                const certData = {
                    reportType: 'governanceReviewCertificate',
                    schemaVersion: log.schemaVersion || '1.2.0',
                    reviewLogId: log.id,
                    reviewerId: log.reviewerId || null,
                    reviewerEmail: log.reviewerEmail || null,
                    overallAssessment: log.overallAssessment,
                    totalEventsInPeriod: log.totalEventsInPeriod || 0,
                    highCriticalInPeriod: log.highCriticalInPeriod || 0,
                    sampleSize: log.sampleSize || 0,
                    itemsConfirmedClean: log.itemsConfirmedClean || 0,
                    findingsCount: log.findingsCount || 0,
                    findingsDetails: log.findingsDetails || null,
                    autoGenerated: log.autoGenerated || false,
                    createdAt: log.createdAt?.toDate ? log.createdAt.toDate().toISOString() : log.createdAt || null,
                    periodStart: log.periodStart?.toDate ? log.periodStart.toDate().toISOString() : log.periodStart || null,
                    periodEnd: log.periodEnd?.toDate ? log.periodEnd.toDate().toISOString() : log.periodEnd || null,
                };
                blob = new Blob([JSON.stringify(certData, null, 2)], { type: 'application/json' });
                filename = `${filenameBase}.json`;

            } else if (format === 'word') {
                const assessmentLabel = log.overallAssessment === 'controls_effective'
                    ? 'Controls Effective'
                    : 'Finding Identified';
                const docx = new Document({
                    sections: [{
                        children: [
                            new Paragraph({ text: 'AI Governance Review Certificate', heading: HeadingLevel.HEADING_1 }),
                            new Paragraph({ children: [new TextRun({ text: `Advisor: ${log.reviewerEmail || 'Unknown'}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Review Period: ${periodLabel}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Assessment: ${assessmentLabel}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Total Events in Period: ${log.totalEventsInPeriod || 0}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `HIGH / CRITICAL Events: ${log.highCriticalInPeriod || 0}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Events Sampled: ${log.sampleSize || 0}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Items Confirmed Clean: ${log.itemsConfirmedClean || 0}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Findings Count: ${log.findingsCount || 0}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Review Type: ${log.autoGenerated ? 'Auto-Generated' : 'Manual Review'}` })] }),
                            new Paragraph({ children: [new TextRun({ text: `Review Log ID: ${log.id}` })] }),
                            ...(log.findingsDetails ? [
                                new Paragraph({ text: 'Finding Details', heading: HeadingLevel.HEADING_2 }),
                                new Paragraph({ children: [new TextRun({ text: log.findingsDetails })] }),
                            ] : []),
                            new Paragraph({ text: '' }),
                            new Paragraph({ children: [new TextRun({ text: 'Generated by SavvaiEdge™ — Savvai Inc. For SOC 2 compliance documentation purposes.', color: '666666', size: 16 })] }),
                        ]
                    }]
                });
                blob = await Packer.toBlob(docx);
                filename = `${filenameBase}.docx`;

            } else {
                throw new Error(`Unsupported export format: ${format}`);
            }

            const url = window.URL.createObjectURL(blob);
            try {
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success(`Review certificate exported to ${format.toUpperCase()}`);
            } finally {
                window.URL.revokeObjectURL(url);
            }

        } catch (error) {
            console.error('Review certificate export failed:', error);
            toast.error(`Certificate export failed: ${error.message || 'Please try again.'}`);
        } finally {
            setCertExportingId(null);
        }
    };

        const handleExport = async () => {
        if (activeView === 'accesslog') {
            try {
                setIsExporting(true);
                const filenameBase = `Evidence-Access-Log-${dateFrom}-to-${dateTo}`;

                const getDateRangeDisplay = (log) => {
                    if (log.reportType === 'review_certificate') return log.reviewPeriod || '—';
                    if (log.dateRange?.from && log.dateRange?.to) return `${log.dateRange.from} to ${log.dateRange.to}`;
                    return '—';
                };

                let blob, filename;

                if (exportFormat === 'csv') {
                    const rows = [['Date', 'Action', 'User', 'Format', 'Recipient', 'Date Range', 'Items']];
                    accessLogs.forEach(log => {
                        rows.push([
                            formatAccessLogDate(log.timestamp),
                            log.action === 'governance_export' ? 'Export' : 'Share',
                            log.userEmail || '—',
                            log.format ? log.format.toUpperCase() : '—',
                            log.recipientEmail || '—',
                            getDateRangeDisplay(log),
                            log.reportType === 'review_certificate' ? '—' : (log.itemCount?.toString() || 'N/A')
                        ]);
                    });
                    const csv = rows.map(row =>
                        row.map(cell => {
                            const str = (cell ?? '').toString().replace(/"/g, '""').replace(/[\r\n]+/g, ' ');
                            return str.includes(',') || str.includes('"') ? `"${str}"` : str;
                        }).join(',')
                    ).join('\n');
                    blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    filename = `${filenameBase}.csv`;

                } else if (exportFormat === 'json') {
                    const exportData = {
                        reportType: 'evidenceAccessLog',
                        generatedBy: user?.email || 'Unknown',
                        exportedAt: new Date().toISOString(),
                        dateRange: { from: dateFrom, to: dateTo },
                        entries: accessLogs.map(log => ({
                            date: formatAccessLogDate(log.timestamp),
                            action: log.action === 'governance_export' ? 'Export' : 'Share',
                            user: log.userEmail || '—',
                            format: log.format ? log.format.toUpperCase() : '—',
                            recipient: log.recipientEmail || '—',
                            dateRange: getDateRangeDisplay(log),
                            items: log.reportType === 'review_certificate' ? '—' : (log.itemCount?.toString() || 'N/A')
                        }))
                    };
                    blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json;charset=utf-8;' });
                    filename = `${filenameBase}.json`;

                } else if (exportFormat === 'word') {
                    const headerRow = new DocxTableRow({
                        children: ['Date', 'Action', 'User', 'Format', 'Recipient', 'Date Range', 'Items'].map(h =>
                            new DocxTableCell({ children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })] })
                        )
                    });
                    const dataRows = accessLogs.map(log => new DocxTableRow({
                        children: [
                            formatAccessLogDate(log.timestamp),
                            log.action === 'governance_export' ? 'Export' : 'Share',
                            log.userEmail || '—',
                            log.format ? log.format.toUpperCase() : '—',
                            log.recipientEmail || '—',
                            getDateRangeDisplay(log),
                            log.reportType === 'review_certificate' ? '—' : (log.itemCount?.toString() || 'N/A')
                        ].map(val => new DocxTableCell({ children: [new Paragraph({ text: val })] }))
                    }));
                    const docx = new Document({
                        sections: [{
                            children: [
                                new Paragraph({ text: 'Evidence Access Log', heading: HeadingLevel.HEADING_1 }),
                                new Paragraph({ children: [new TextRun({ text: `Generated By: ${user?.email || 'Unknown'}` })] }),
                                new Paragraph({ children: [new TextRun({ text: `Date Range: ${dateFrom} to ${dateTo}` })] }),
                                new Paragraph({ children: [new TextRun({ text: `Exported: ${new Date().toLocaleString()}` })] }),
                                new Paragraph({ text: '' }),
                                new DocxTable({ rows: [headerRow, ...dataRows], width: { size: 100, type: WidthType.PERCENTAGE } })
                            ]
                        }]
                    });
                    blob = await Packer.toBlob(docx);
                    filename = `${filenameBase}.docx`;

                } else if (exportFormat === 'pdf') {
                    const generatePDFFn = httpsCallable(functions, 'generateGovernancePDF');
                    const result = await generatePDFFn({
                        reportType: 'accessLog',
                        generatedBy: user?.email || 'Unknown',
                        dateRange: { from: dateFrom, to: dateTo },
                        accessLogEntries: accessLogs.map(log => ({
                            date: formatAccessLogDate(log.timestamp),
                            action: log.action === 'governance_export' ? 'Export' : 'Share',
                            generatedBy: log.userEmail || '—',
                            format: log.format ? log.format.toUpperCase() : '—',
                            recipient: log.recipientEmail || '—',
                            dateRange: getDateRangeDisplay(log),
                            items: log.reportType === 'review_certificate' ? '—' : (log.itemCount?.toString() || 'N/A')
                        }))
                    });
                    if (!result.data?.success || !result.data?.pdfBase64) {
                        throw new Error(result.data?.error || 'PDF generation failed');
                    }
                    const byteCharacters = atob(result.data.pdfBase64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
                    filename = result.data?.filename || `${filenameBase}.pdf`;
                }

                const url = window.URL.createObjectURL(blob);
                try {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success(`Evidence access log exported to ${exportFormat.toUpperCase()}`);
                    setExportModalOpen(false);
                } finally {
                    window.URL.revokeObjectURL(url);
                }

                try {
                    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
                    await addDoc(collection(db, 'auditLogs'), {
                        action: 'governance_export',
                        reportType: 'access_log',
                        format: exportFormat,
                        dateRange: { from: dateFrom, to: dateTo },
                        itemCount: accessLogs.length,
                        userId: user?.uid,
                        userEmail: user?.email,
                        organizationId: user?.organizationId,
                        timestamp: serverTimestamp()
                    });
                    fetchAccessLogs();
                } catch (logError) {
                    console.error('Failed to log access log export event:', logError);
                }
            } catch (error) {
                console.error('Access log export failed:', error);
                toast.error(`Export failed: ${error.message || 'Please try again.'}`);
            } finally {
                setIsExporting(false);
            }
            return;
        }

        try {
            setIsExporting(true);
            const clientSlug = clientFilter === 'all'
                ? 'All-Clients'
                : (selectedClientName || 'Selected-Client').replace(/[^a-zA-Z0-9-_]+/g, '-');
            const filenameBase = `AI-Governance-${clientSlug}-${dateFrom}-to-${dateTo}`;
            let blob, filename;

            if (exportFormat === 'csv') {
                blob = new Blob([generateCSV()], { type: 'text/csv;charset=utf-8;' });
                filename = `${filenameBase}.csv`;
            } else if (exportFormat === 'word') {
                blob = await generateWordDocument();
                filename = `${filenameBase}.docx`;
            } else if (exportFormat === 'json') {
                blob = new Blob([generateJSON()], { type: 'application/json;charset=utf-8;' });
                filename = `${filenameBase}.json`;
            } else if (exportFormat === 'pdf') {
                // Call Cloud Function for PDF generation
                const generatePDFFn = httpsCallable(functions, 'generateGovernancePDF');
                                const result = await generatePDFFn({
                    organizationName: orgData?.name || 'N/A',
                    clientScopeId: clientFilter === 'all' ? null : clientFilter,
                    clientScopeName: selectedClientName,
                    dateRange: { from: dateFrom, to: dateTo },
                    summaryMetrics,
                    reviewMetrics,
                    anonymizationMetrics,
                    providerBreakdown,
                    contentTypeBreakdown,
                    auditItems: filteredAuditItems.map(item => ({
                        date: formatDate(item),
                        type: item.typeLabel,
                        client: item.clientName,
                        provider: item.provider,
                        wasFallback: item.wasFallback,
                        reviewStatus: item.isGovernanceOnly ? 'Auto-Governed' : (item.reviewStatus || 'Pending'),
                        reviewedBy: item.reviewedByEmail || null,
                        historyCount: item.reviewHistory?.length || 0,
                        synthesisModel: item.aiAuditMetadata?.synthesisModel || null,
                        synthesisModelLabel: item.aiAuditMetadata?.synthesisModelLabel || item.provider || null,
                        researchModel: item.aiAuditMetadata?.researchModel || null,
                        description: item.questionPreview || ''
                    })),
                    totalAuditItems: filteredAuditItems.length,
                    generatedBy: user?.email || 'Unknown',
                    governanceEventsData: filteredGovernanceEvents.slice(0, 100).map(event => ({
                        timestamp: event.timestamp?.toDate?.()
                            ? event.timestamp.toDate().toLocaleString()
                            : event.timestamp ? new Date(event.timestamp).toLocaleString() : '—',
                        dataPath: event.dataPath,
                        riskLevel: event.riskLevel,
                        action: event.action,
                        method: event.method,
                        totalPiiFound: event.totalPiiFound || 0
                    })),
                    reviewQueueData: filteredReviewQueueItems.map(item => ({
                        createdAt: item.createdAt?.toDate?.()
                            ? item.createdAt.toDate().toLocaleString()
                            : item.createdAt ? new Date(item.createdAt).toLocaleString() : '—',
                        dataPath: item.dataPath,
                        riskLevel: item.riskLevel,
                        status: item.status,
                        reviewedBy: item.reviewedBy || '—',
                        reviewNotes: item.reviewNotes || '—'
                    }))
                });

                if (!result.data.success) {
                    throw new Error(result.data.error || 'PDF generation failed');
                }

                // Convert base64 to blob
                const byteCharacters = atob(result.data.pdfBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                blob = new Blob([byteArray], { type: 'application/pdf' });
                filename = `${filenameBase}.pdf`;
            }

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
            toast.success(`Governance report exported to ${exportFormat.toUpperCase()}`);
            setExportModalOpen(false);

            // Log export event for audit purposes
            try {
                const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
                                await addDoc(collection(db, 'auditLogs'), {
                    action: 'governance_export',
                    format: exportFormat,
                    dateRange: { from: dateFrom, to: dateTo },
                    clientScopeId: clientFilter === 'all' ? null : clientFilter,
                    clientScopeName: selectedClientName,
                    itemCount: filteredAuditItems.length,
                    userId: user?.uid,
                    userEmail: user?.email,
                    organizationId: user?.organizationId,
                    organizationName: orgData?.name || null,
                    timestamp: serverTimestamp()
                });
                // Refresh access logs to show the new entry
                fetchAccessLogs();
            } catch (logError) {
                console.error('Failed to log export event:', logError);
                // Non-blocking - don't fail the export if logging fails
            }
        } catch (error) {
            console.error('Export failed:', error);
            toast.error(`Export failed: ${error.message || 'Please try again.'}`);
        } finally {
            setIsExporting(false);
        }
    };

    // Handle share
    const handleShare = async (shareData) => {
        try {
            const shareGovernanceReportFn = httpsCallable(functions, 'shareGovernanceReport');
            const result = await shareGovernanceReportFn({
                recipientEmail: shareData.recipientEmail,
                recipientName: shareData.recipientName,
                personalMessage: shareData.customMessage || shareData.personalMessage || '',
                senderName: user?.displayName || user?.name || user?.email || 'Firm Admin',
                dateRange: { from: dateFrom, to: dateTo },
                summaryMetrics,
                reviewMetrics,
                providerBreakdown,
                contentTypeBreakdown,
                auditItemCount: filteredAuditItems.length
            });
            
            if (result.data.success) {
                // Log share event for audit purposes
                try {
                    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
                    await addDoc(collection(db, 'auditLogs'), {
                        action: 'governance_share',
                        recipientEmail: shareData.recipientEmail,
                        dateRange: { from: dateFrom, to: dateTo },
                        clientScopeId: clientFilter === 'all' ? null : clientFilter,
                        clientScopeName: selectedClientName,
                        itemCount: filteredAuditItems.length,
                        userId: user?.uid,
                        userEmail: user?.email,
                        organizationId: user?.organizationId,
                        organizationName: orgData?.name || null,
                        timestamp: serverTimestamp()
                    });
                    // Refresh access logs to show the new entry
                    fetchAccessLogs();
                } catch (logError) {
                    console.error('Failed to log share event:', logError);
                    // Non-blocking
                }
                return result.data;
            }
            throw new Error(result.data.error || 'Failed to share governance report.');
        } catch (error) {
            console.error('Error sharing governance report:', error);
            throw error;
        }
    };

    // Stat card component
    const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h3" color={`${color}.main`} fontWeight={600}>
                            {value}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={500}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Icon sx={{ fontSize: 40, color: `${color}.light`, opacity: 0.8 }} />
                </Box>
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading governance data...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            {/* Header */}
            <Typography variant="h4" gutterBottom>
                AI Governance
            </Typography>

            {/* Controls Row - matches Analytics layout */}
            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }} flexWrap="wrap">
                <Typography variant="body1" color="text.secondary">
                    Audit trail and model usage tracking for compliance reporting.
                </Typography>
                <TextField
                    label="Date From"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                                <TextField
                    label="Date To"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                                <FormControl
                    size="small"
                    sx={{ minWidth: 220 }}
                    disabled={activeView === 'reviewcompliance'}
                >
                    <InputLabel>Client Scope</InputLabel>
                    <Select
                        value={clientFilter}
                        label="Client Scope"
                        onChange={(e) => {
                            setClientFilter(e.target.value);
                            setPage(0);
                        }}
                    >
                        <MenuItem value="all">All Clients</MenuItem>
                        {clientOptions.map(client => (
                            <MenuItem key={client.id} value={client.id}>
                                {client.name || client.displayName || client.id}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => {
                        setDateFrom(getDefaultDateFrom());
                        setDateTo(getDefaultDateTo());
                        setClientFilter('all');
                    }}
                >
                    Reset
                </Button>
                                <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => setExportModalOpen(true)}
                    disabled={activeView === 'reviewcompliance'}
                >
                    Export
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareModalOpen(true)}
                    disabled={activeView === 'accesslog' || activeView === 'reviewcompliance'}
                >
                    Share
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchGovernanceData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* View Toggle */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <ToggleButtonGroup
                    value={activeView}
                    exclusive
                    onChange={(e, newView) => newView && setActiveView(newView)}
                    size="small"
                >
                    <ToggleButton value="summary">Summary</ToggleButton>
                    <ToggleButton value="audit">Audit Trail</ToggleButton>
                    <ToggleButton value="reviewcompliance" onClick={() => { if (!complianceData) fetchReviewCompliance(); }}>
                        Review Compliance
                    </ToggleButton>
                    <ToggleButton value="accesslog">Access Log</ToggleButton>
                </ToggleButtonGroup>
            </Paper>

            {/* SUMMARY VIEW */}
            {activeView === 'summary' && (
                <Box>
                    {/* QW3: Governance Review Queue Alert */}
                    {filteredReviewQueueItems.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 3 }} icon={<PendingIcon />}>
                            <strong>{filteredReviewQueueItems.length} item{filteredReviewQueueItems.length !== 1 ? 's' : ''}</strong> in the governance review queue require admin attention. 
                            These were flagged as HIGH or CRITICAL risk by the DLP governance pipeline.
                        </Alert>
                    )}

                    {/* Metric Cards */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Total Items"
                                value={summaryMetrics.total.toLocaleString()}
                                subtitle="AI-generated content"
                                icon={AIIcon}
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Primary Success"
                                value={`${summaryMetrics.primarySuccessRate}%`}
                                subtitle="Primary model used"
                                icon={SuccessIcon}
                                color="success"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Fallbacks"
                                value={summaryMetrics.fallbacks}
                                subtitle="Required backup model"
                                icon={FallbackIcon}
                                color={summaryMetrics.fallbacks > 0 ? 'warning' : 'success'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Active Models"
                                value={summaryMetrics.activeModels}
                                subtitle="Distinct AI providers"
                                icon={ModelsIcon}
                                color="info"
                            />
                        </Grid>
                    </Grid>

                    {/* P2: Review Compliance Section */}
                    <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                        Human Review Compliance
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <StatCard
                                title="Review Rate"
                                value={`${reviewMetrics.overall.reviewRate}%`}
                                subtitle={`${reviewMetrics.overall.reviewed} of ${reviewMetrics.overall.total} reviewed`}
                                icon={ReviewIcon}
                                color={parseFloat(reviewMetrics.overall.reviewRate) >= 90 ? 'success' : parseFloat(reviewMetrics.overall.reviewRate) >= 70 ? 'warning' : 'error'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <StatCard
                                title="Pending Review"
                                value={reviewMetrics.overall.total - reviewMetrics.overall.reviewed}
                                subtitle="Awaiting professional review"
                                icon={PendingIcon}
                                color={(reviewMetrics.overall.total - reviewMetrics.overall.reviewed) > 0 ? 'warning' : 'success'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <StatCard
                                title="Approved"
                                value={reviewMetrics.overall.approved}
                                subtitle={`${reviewMetrics.overall.approvalRate}% of reviewed`}
                                icon={ApprovedIcon}
                                color="success"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Review by Content Type
                                    </Typography>
                                    <Box sx={{ mt: 1 }}>
                                                                                {['qa', 'signal', 'decision_memo', 'financial_intelligence'].map(type => {
                                            const data = reviewMetrics.byType[type];
                                            const rate = data.total > 0 ? ((data.reviewed / data.total) * 100).toFixed(0) : 0;
                                            const label = type === 'qa' ? 'Q&A' : type === 'signal' ? 'Signals' : type === 'decision_memo' ? 'Decision Memos' : 'FI Reports';
                                            return (
                                                <Box key={type} display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                                    <Typography variant="body2">{label}</Typography>
                                                    <Chip 
                                                        label={`${rate}%`} 
                                                        size="small" 
                                                        color={parseFloat(rate) >= 90 ? 'success' : parseFloat(rate) >= 70 ? 'warning' : 'default'}
                                                        variant={parseFloat(rate) >= 90 ? 'filled' : 'outlined'}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
</Grid>

{/* P3: Data Protection Compliance Section */}
<Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
  Data Protection Compliance
</Typography>

{anonymizationMetrics.total === 0 ? (
  <Paper sx={{ p: 3, mb: 4, textAlign: 'center', bgcolor: 'background.default' }}>
    <DataProtectionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
    <Typography variant="body1" color="text.secondary">
      No AI artifacts generated yet in this period.
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Anonymization metrics will appear once Q&A, SavvaiSignals, or Decision Memos are created.
    </Typography>
  </Paper>
) : (
  <Grid container spacing={2} sx={{ mb: 4 }}>
    <Grid item xs={12} sm={6} md={3} lg={2.4}>
      <StatCard
        title="Compliance Rate"
        value={`${anonymizationMetrics.complianceRate}%`}
        subtitle={`${anonymizationMetrics.totalWithProof} of ${anonymizationMetrics.total} with proof`}
        icon={DataProtectionIcon}
        color={parseFloat(anonymizationMetrics.complianceRate) >= 100 ? 'success' : parseFloat(anonymizationMetrics.complianceRate) >= 90 ? 'warning' : 'error'}
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3} lg={2.4}>
      <StatCard
        title="PII Patterns Scrubbed"
        value={anonymizationMetrics.piiPatternsTotal.toLocaleString()}
        subtitle="Detected & removed"
        icon={DataProtectionIcon}
        color="success"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3} lg={2.4}>
      <StatCard
        title="Detailed Metrics"
        value={anonymizationMetrics.totalWithDetailedMetrics}
        subtitle={`${anonymizationMetrics.totalBackfilled} backfilled`}
        icon={DataProtectionIcon}
        color="info"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3} lg={2.4}>
      <Card elevation={2}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            PII Removed by Type
          </Typography>
          <Box sx={{ mt: 1 }}>
            {[
              { label: 'Company Names', value: anonymizationMetrics.breakdown.companyNames },
              { label: 'Email Addresses', value: anonymizationMetrics.breakdown.emails },
              { label: 'Phone Numbers', value: anonymizationMetrics.breakdown.phones },
              { label: 'Addresses', value: anonymizationMetrics.breakdown.addresses },
              { label: 'SSNs', value: anonymizationMetrics.breakdown.ssns }
            ].map(item => (
              <Box key={item.label} display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2">{item.label}</Typography>
                <Chip
                  label={item.value}
                  size="small"
                  color={item.value > 0 ? 'success' : 'default'}
                  variant={item.value > 0 ? 'filled' : 'outlined'}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
)}

{/* QW2: DLP Governance Pipeline Section */}
<Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
    DLP Governance Pipeline
</Typography>

{governanceMetrics.total === 0 ? (
    <Paper sx={{ p: 3, mb: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <DataProtectionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
            No governance events recorded yet in this period.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Events are logged when data passes through the DLP governance pipeline — each governed data path will appear automatically as events are recorded.
        </Typography>
    </Paper>
) : (
    <>
        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="Governance Events"
                    value={governanceMetrics.total.toLocaleString()}
                    subtitle={`Across ${Object.keys(governanceMetrics.dataPathCounts).length} data path${Object.keys(governanceMetrics.dataPathCounts).length !== 1 ? 's' : ''}`}
                    icon={DataProtectionIcon}
                    color="primary"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="DLP API Rate"
                    value={`${governanceMetrics.dlpRate}%`}
                    subtitle={`${governanceMetrics.methodCounts.dlp} DLP, ${governanceMetrics.methodCounts.regex} regex`}
                    icon={SuccessIcon}
                    color={parseFloat(governanceMetrics.dlpRate) >= 90 ? 'success' : 'warning'}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="Clean Rate"
                    value={`${governanceMetrics.cleanRate}%`}
                    subtitle="CLEAR + LOW risk"
                    icon={SuccessIcon}
                    color={parseFloat(governanceMetrics.cleanRate) >= 90 ? 'success' : parseFloat(governanceMetrics.cleanRate) >= 70 ? 'warning' : 'error'}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="Pending Reviews"
                    value={filteredReviewQueueItems.length}
                    subtitle="HIGH/CRITICAL risk items"
                    icon={PendingIcon}
                    color={filteredReviewQueueItems.length > 0 ? 'warning' : 'success'}
                />
            </Grid>
        </Grid>

        {/* Data Path Activity */}
        <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Governance by Data Path
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
                {Object.entries(governanceMetrics.dataPathCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([path, count]) => (
                        <Chip key={path} label={`${formatDataPath(path)}: ${count}`} size="small" color="primary" variant="outlined" />
                    ))
                }
            </Box>
        </Paper>

        {/* Risk Distribution */}
        <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Risk Distribution
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
                {Object.entries(governanceMetrics.riskCounts)
                    .filter(([, count]) => count > 0)
                    .map(([level, count]) => (
                        <Chip
                            key={level}
                            label={`${level}: ${count}`}
                            size="small"
                            color={level === 'CLEAR' || level === 'LOW' ? 'success' : level === 'MEDIUM' ? 'warning' : 'error'}
                            variant={level === 'HIGH' || level === 'CRITICAL' ? 'filled' : 'outlined'}
                        />
                    ))
                }
            </Box>
        </Paper>

        {/* PII Types Detected */}
        {Object.keys(governanceMetrics.piiTypeCounts).length > 0 && (
            <Paper sx={{ p: 2, mb: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    PII Types Detected &amp; Scrubbed
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                    {Object.entries(governanceMetrics.piiTypeCounts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([type, count]) => (
                            <Chip key={type} label={`${type.replace(/_/g, ' ')}: ${count}`} size="small" color="info" variant="outlined" />
                        ))
                    }
                </Box>
            </Paper>
        )}
    </>
)}

{/* Charts Row */}
                    <Box display="flex" gap={3} sx={{ mb: 4 }}>
                        {/* Provider Breakdown Bar Chart */}
                        <Paper sx={{ p: 3, height: 400, flex: 2, minWidth: 0 }}>
                            <Typography variant="h6" gutterBottom>
                                Provider Usage Breakdown
                            </Typography>
                            {providerBreakdown.length === 0 ? (
                                <Box display="flex" alignItems="center" justifyContent="center" height={300}>
                                    <Typography color="text.secondary">No data for selected period</Typography>
                                </Box>
                            ) : (
                                <Box height={320}>
                                    <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                                        <BarChart 
                                            data={providerBreakdown} 
                                            layout="vertical"
                                            margin={{ left: 20, right: 30 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" />
                                            <YAxis 
                                                type="category" 
                                                dataKey="name" 
                                                width={180}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <RechartsTooltip 
                                                formatter={(value, name, props) => [
                                                    `${value} items (${props.payload.percentage}%)`,
                                                    props.payload.role || 'Usage'
                                                ]}
                                            />
                                            <Bar dataKey="value" name="Items">
                                                {providerBreakdown.map((entry, index) => (
                                                    <Cell 
                                                        key={`cell-${index}`} 
                                                        fill={PROVIDER_COLORS[entry.name] || DEFAULT_COLOR}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </Paper>

                        {/* Content Type Pie Chart */}
                        <Paper sx={{ p: 3, height: 400, flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" gutterBottom>
                                By Content Type
                            </Typography>
                            {contentTypeBreakdown.length === 0 ? (
                                <Box display="flex" alignItems="center" justifyContent="center" height={300}>
                                    <Typography color="text.secondary">No data</Typography>
                                </Box>
                            ) : (
                                <Box height={320}>
                                    <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                                        <PieChart>
                                            <Pie
                                                data={contentTypeBreakdown}
                                                cx="50%"
                                                cy="45%"
                                                innerRadius={50}
                                                outerRadius={80}
                                                paddingAngle={2}
                                                dataKey="value"
                                                label={({ name, value }) => `${name}: ${value}`}
                                                labelLine={{ length: 10, length2: 5 }}
                                            >
                                                <Cell fill="#6366f1" />
                                                <Cell fill="#10b981" />
                                                <Cell fill="#f59e0b" />
                                            </Pie>
                                            <RechartsTooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {/* Data Infrastructure Note */}
                    <Paper sx={{ p: 2, mt: 3, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.main' }}>
                        <Typography variant="body2" color="info.dark">
                            <strong>Note:</strong> This dashboard provides the data infrastructure for your compliance program. 
                            Your organization's compliance team uses this audit trail to build attestations for SOC 2, 
                            regulatory inquiries, or client audits. SavvaiEdge provides the evidence — you own the compliance claims.
                        </Typography>
                    </Paper>
                </Box>
            )}

            {/* AUDIT TRAIL VIEW */}
            {activeView === 'audit' && (
                <Box>
                    {/* Filters */}
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <FilterIcon color="action" />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Content Type</InputLabel>
                                    <Select
                                        value={contentTypeFilter}
                                        label="Content Type"
                                        onChange={(e) => { setContentTypeFilter(e.target.value); setPage(0); }}
                                    >
                                        <MenuItem value="all">All Types</MenuItem>
                                        <MenuItem value="qa">Q&A</MenuItem>
                                        <MenuItem value="signal">SavvaiSignals</MenuItem>
                                        <MenuItem value="decision_memo">Decision Memos</MenuItem>
                                        <MenuItem value="financial_intelligence">Financial Intelligence</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Provider</InputLabel>
                                    <Select
                                        value={providerFilter}
                                        label="Provider"
                                        onChange={(e) => { setProviderFilter(e.target.value); setPage(0); }}
                                    >
                                        <MenuItem value="all">All Providers</MenuItem>
                                        <MenuItem disabled sx={{ fontWeight: 'bold', opacity: 1 }}>— Synthesis —</MenuItem>
                                        {uniqueSynthesisProviders.map(p => (
                                            <MenuItem key={`synth-${p}`} value={p}>{p}</MenuItem>
                                        ))}
                                        {uniqueResearchProviders.length > 0 && (
                                            <MenuItem disabled sx={{ fontWeight: 'bold', opacity: 1 }}>— Research —</MenuItem>
                                        )}
                                        {uniqueResearchProviders.map(p => (
                                            <MenuItem key={`research-${p}`} value={`research:${p}`}>{p}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Fallback</InputLabel>
                                    <Select
                                        value={fallbackFilter}
                                        label="Fallback"
                                        onChange={(e) => { setFallbackFilter(e.target.value); setPage(0); }}
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="no">Primary Only</MenuItem>
                                        <MenuItem value="yes">Fallbacks Only</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* P2: Review status filter */}
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Review Status</InputLabel>
                                    <Select
                                        value={reviewStatusFilter}
                                        label="Review Status"
                                        onChange={(e) => { setReviewStatusFilter(e.target.value); setPage(0); }}
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="pending">Pending Review</MenuItem>
                                        <MenuItem value="reviewed">Any Reviewed</MenuItem>
                                        <MenuItem value="approved">Approved</MenuItem>
                                        <MenuItem value="rejected">Rejected</MenuItem>
                                        <MenuItem value="draft">Draft</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs />
                            <Grid item>
                                <Chip 
                                    label={`${filteredAuditItems.length} items`} 
                                    color="primary" 
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Audit Table */}
                    <Paper>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell sx={{ fontWeight: 600, width: 40 }}></TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Fallback</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Review Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Reviewer</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}># Reviews</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Data Protection</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Synthesis Model</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Research Model</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAuditItems.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    No items match the selected filters.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredAuditItems
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((item) => (
                                                <React.Fragment key={item.id}>
                                                    <TableRow hover sx={{ '& > *': { borderBottom: expandedRows.has(item.id) ? 'none' : undefined } }}>
                                                        {/* P5: Expand/Collapse button */}
                                                        <TableCell sx={{ width: 40, p: 0.5 }}>
                                                            {item.reviewHistory && item.reviewHistory.length > 0 ? (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => toggleRowExpand(item.id)}
                                                                >
                                                                    {expandedRows.has(item.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                </IconButton>
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {formatDate(item)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={item.typeLabel} 
                                                                size="small"
                                                                color={
                                                                item.type === 'qa' ? 'primary' :
                                                                item.type === 'signal' ? 'success' :
                                                                item.type === 'governance_event' ? 'info' :
                                                                'warning'
                                                                }
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {item.clientName}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.wasFallback ? (
                                                                <Chip 
                                                                    label="Yes" 
                                                                    size="small" 
                                                                    color="warning"
                                                                />
                                                            ) : (
                                                                <Chip 
                                                                    label="No" 
                                                                    size="small" 
                                                                    color="success"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </TableCell>
                                                        {/* Review Status Column */}
                                                        <TableCell>
                                                            {item.isGovernanceOnly ? (
                                                                <Chip
                                                                    label="Auto-Governed"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="info"
                                                                />
                                                            ) : item.reviewStatus ? (
                                                                <Chip 
                                                                    label={item.reviewStatus.charAt(0).toUpperCase() + item.reviewStatus.slice(1)}
                                                                    size="small"
                                                                    color={
                                                                        item.reviewStatus === 'approved' ? 'success' :
                                                                        item.reviewStatus === 'rejected' ? 'error' :
                                                                        'warning'
                                                                    }
                                                                />
                                                            ) : (
                                                                <Chip 
                                                                    label="Pending" 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    color="default"
                                                                />
                                                            )}
                                                        </TableCell>
                                                        {/* P5: Reviewer Column */}
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.reviewedByEmail || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        {/* P5: # Reviews Column */}
                                                        <TableCell>
                                                            {item.reviewHistory && item.reviewHistory.length > 0 ? (
                                                                <Chip 
                                                                    label={item.reviewHistory.length}
                                                                    size="small"
                                                                    color="info"
                                                                    variant="outlined"
                                                                    onClick={() => toggleRowExpand(item.id)}
                                                                    sx={{ cursor: 'pointer' }}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                                            )}
                                                        </TableCell>
                                                        {/* Data Protection Column */}
                                                        <TableCell>
                                                            {(item.aiAuditMetadata?.anonymizationApplied || item.governanceResult?.governed) ? (
                                                                <Tooltip title={
                                                                    <Box>
                                                                        <Typography variant="caption" display="block" fontWeight={600}>
                                                                            Data Protection Applied
                                                                        </Typography>
                                                                        {item.governanceResult?.governed ? (
                                                                            <Box sx={{ mt: 0.5 }}>
                                                                                <Typography variant="caption" display="block">
                                                                                    Method: {item.governanceResult.method === 'dlp' ? 'Google Cloud DLP' : 'Regex fallback'}
                                                                                </Typography>
                                                                                <Typography variant="caption" display="block">
                                                                                    Risk: {item.governanceResult.riskLevel || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="caption" display="block">
                                                                                    PII found: {item.governanceResult.totalPiiFound || 0}
                                                                                </Typography>
                                                                                <Typography variant="caption" display="block">
                                                                                    Action: {item.governanceResult.action || 'N/A'}
                                                                                </Typography>
                                                                            </Box>
                                                                        ) : (
                                                                            <Box>
                                                                                <Typography variant="caption" display="block">
                                                                                    {item.aiAuditMetadata?.anonymizationTimestamp 
                                                                                        ? new Date(item.aiAuditMetadata.anonymizationTimestamp).toLocaleString()
                                                                                        : 'Timestamp not recorded'}
                                                                                </Typography>
                                                                                {item.aiAuditMetadata?.anonymizationDetails && !item.aiAuditMetadata.anonymizationDetails.backfilled && (
                                                                                    <Box sx={{ mt: 0.5 }}>
                                                                                        {item.aiAuditMetadata.anonymizationDetails.companyNameRemoved && (
                                                                                            <Typography variant="caption" display="block">
                                                                                                ✓ Company name anonymized
                                                                                            </Typography>
                                                                                        )}
                                                                                        <Typography variant="caption" display="block">
                                                                                            PII patterns removed: {item.aiAuditMetadata.anonymizationDetails.piiPatternsDetected || 0}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                )}
                                                                                {item.aiAuditMetadata?.anonymizationDetails?.backfilled && (
                                                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                                                                        (Backfilled from legacy data)
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                }>
                                                                    <Chip 
                                                                        label={item.governanceResult?.governed ? (item.governanceResult.method === 'dlp' ? 'DLP ✓' : 'Regex ✓') : '✓'} 
                                                                        size="small" 
                                                                        color={item.governanceResult?.governed ? (item.governanceResult.method === 'dlp' ? 'success' : 'warning') : 'success'}
                                                                        variant="outlined"
                                                                        sx={{ cursor: 'help' }}
                                                                    />
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                                            )}
                                                        </TableCell>
                                                        {/* Synthesis Model */}
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.aiAuditMetadata?.synthesisModelLabel || item.provider || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        {/* Research Model */}
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.aiAuditMetadata?.researchModel === 'sonar-pro' ? 'Perplexity Sonar Pro'
                                                                    : item.aiAuditMetadata?.researchModel === 'sonar' ? 'Perplexity Sonar'
                                                                    : item.aiAuditMetadata?.researchModel || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ maxWidth: 250 }}>
                                                            <Tooltip 
                                                                title={item.questionPreview || 'No description'} 
                                                                placement="top-start"
                                                                arrow
                                                            >
                                                                <Typography 
                                                                    variant="body2" 
                                                                    color="text.secondary"
                                                                    sx={{ 
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        cursor: 'help'
                                                                    }}
                                                                >
                                                                    {item.questionPreview}
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                    {/* P5: Expandable Review History Row */}
                                                    {item.reviewHistory && item.reviewHistory.length > 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={12} sx={{ py: 0, bgcolor: 'grey.50' }}>
                                                                <Collapse in={expandedRows.has(item.id)} timeout="auto" unmountOnExit>
                                                                    <Box sx={{ py: 2, px: 3 }}>
                                                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                            Review History
                                                                        </Typography>
                                                                        <Box sx={{ pl: 1, borderLeft: '3px solid #e0e0e0' }}>
                                                                            {[...item.reviewHistory].reverse().map((entry, index) => (
                                                                                <Box 
                                                                                    key={index} 
                                                                                    sx={{ 
                                                                                        mb: 1.5, 
                                                                                        pb: 1.5, 
                                                                                        borderBottom: index < item.reviewHistory.length - 1 ? '1px solid #f0f0f0' : 'none' 
                                                                                    }}
                                                                                >
                                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                        {entry.timestamp?.toDate ? 
                                                                                            entry.timestamp.toDate().toLocaleString() : 
                                                                                            entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                                                                                    </Typography>
                                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                        {entry.action === 'status_set' ? (
                                                                                            <span style={{ color: '#10b981' }}>● Initial Review</span>
                                                                                        ) : entry.action === 'status_changed' ? (
                                                                                            <span style={{ color: '#f59e0b' }}>↻ Status Changed</span>
                                                                                        ) : (
                                                                                            <span style={{ color: '#6366f1' }}>✎ Clarification Added</span>
                                                                                        )}
                                                                                        {entry.newStatus && (
                                                                                            <>
                                                                                                {': '}
                                                                                                {entry.previousStatus && (
                                                                                                    <span style={{ textTransform: 'capitalize' }}>
                                                                                                        {entry.previousStatus} → 
                                                                                                    </span>
                                                                                                )}
                                                                                                <span style={{ textTransform: 'capitalize' }}>{entry.newStatus}</span>
                                                                                            </>
                                                                                        )}
                                                                                    </Typography>
                                                                                    {entry.notes && (
                                                                                        <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                                                                                            "{entry.notes}"
                                                                                        </Typography>
                                                                                    )}
                                                                                    {entry.userEmail && (
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            by {entry.userEmail}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Box>
                                                                            ))}
                                                                        </Box>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={filteredAuditItems.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </Paper>
                </Box>
            )}

            {/* REVIEW COMPLIANCE VIEW — Phase C #5 */}
            {activeView === 'reviewcompliance' && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Advisor Review Compliance
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Periodic governance review completion status across all advisors in your organization. Each advisor reviews a sample of AI governance events periodically to verify PII controls.

                    </Typography>

                    {complianceLoading && (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
                            <CircularProgress size={28} sx={{ mr: 2 }} />
                            <Typography color="text.secondary">Loading compliance data...</Typography>
                        </Box>
                    )}

                    {complianceError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            Failed to load compliance data: {complianceError}
                        </Alert>
                    )}

                    {complianceData && !complianceLoading && (
                        <>
                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={12} sm={4}>
                                    <Card elevation={2}>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            <PersonIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                                            <Typography variant="h4" color="primary.main" fontWeight="bold">
                                                {complianceData.totalFcfos}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">Total Advisors</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Card elevation={2}>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            <SuccessIcon sx={{ fontSize: 32, color: complianceData.overallCompletionRate === 100 ? 'success.main' : complianceData.overallCompletionRate >= 50 ? 'warning.main' : 'error.main', mb: 1 }} />
                                            <Typography variant="h4" fontWeight="bold" color={complianceData.overallCompletionRate === 100 ? 'success.main' : complianceData.overallCompletionRate >= 50 ? 'warning.main' : 'error.main'}>
                                                {complianceData.overallCompletionRate}%
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Current Month ({complianceData.currentMonthComplete}/{complianceData.totalFcfos} complete)
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Card elevation={2}>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            <FallbackIcon sx={{ fontSize: 32, color: complianceData.openIncidents > 0 ? 'error.main' : 'success.main', mb: 1 }} />
                                            <Typography variant="h4" fontWeight="bold" color={complianceData.openIncidents > 0 ? 'error.main' : 'success.main'}>
                                                {complianceData.openIncidents}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Open Incidents ({complianceData.totalIncidents} total)
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Per-FCFO Compliance Table */}
                            <Paper sx={{ mb: 4 }}>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell sx={{ width: 40 }} />
                                                <TableCell><strong>Advisor</strong></TableCell>
                                                {complianceData.months.map(m => {
                                                    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                                    return (
                                                        <TableCell key={`${m.year}-${m.month}`} align="center" sx={{ minWidth: 70 }}>
                                                            <strong>{monthNames[m.month]}</strong>
                                                            <br />
                                                            <Typography variant="caption" color="text.secondary">{m.year}</Typography>
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell align="center"><strong>Rate</strong></TableCell>
                                                <TableCell align="center"><strong>Incidents</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {complianceData.fcfoRows.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                                        <Typography color="text.secondary">No advisors found in this organization.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                complianceData.fcfoRows.map(fcfo => (
                                                    <React.Fragment key={fcfo.uid}>
                                                        <TableRow
                                                            hover
                                                            sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedFcfoRows.has(fcfo.uid) ? 'none' : undefined } }}
                                                            onClick={() => setExpandedFcfoRows(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(fcfo.uid)) { next.delete(fcfo.uid); } else { next.add(fcfo.uid); }
                                                                return next;
                                                            })}
                                                        >
                                                            <TableCell sx={{ width: 40 }}>
                                                                <IconButton size="small">
                                                                    {expandedFcfoRows.has(fcfo.uid) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {fcfo.firstName && fcfo.lastName ? `${fcfo.firstName} ${fcfo.lastName}` : fcfo.displayName}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">{fcfo.email}</Typography>
                                                            </TableCell>
                                                            {fcfo.monthStatuses.map((ms, idx) => (
                                                                <TableCell key={idx} align="center">
                                                                    {ms.status === 'complete' ? (
                                                                        <Tooltip title={`${ms.autoGenerated ? 'Auto-generated' : 'Manual review'}: ${ms.assessment === 'controls_effective' ? 'Controls Effective' : 'Finding Identified'}`}>
                                                                            <SuccessIcon
                                                                                fontSize="small"
                                                                                sx={{ color: ms.assessment === 'finding_identified' ? 'warning.main' : 'success.main' }}
                                                                            />
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <Tooltip title="Review not completed">
                                                                            <Typography variant="body2" color="error.main" fontWeight="bold">✗</Typography>
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={`${fcfo.completionRate}%`}
                                                                    size="small"
                                                                    color={fcfo.completionRate === 100 ? 'success' : fcfo.completionRate >= 50 ? 'warning' : 'error'}
                                                                    variant={fcfo.completionRate === 100 ? 'filled' : 'outlined'}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {fcfo.openIncidentCount > 0 ? (
                                                                    <Chip label={fcfo.openIncidentCount} size="small" color="error" />
                                                                ) : (
                                                                    <Chip label={fcfo.incidentCount || '0'} size="small" color="default" variant="outlined" />
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                        {/* Expanded: Individual review logs */}
                                                        {expandedFcfoRows.has(fcfo.uid) && (
                                                            <TableRow>
                                                                <TableCell colSpan={10} sx={{ bgcolor: 'grey.50', py: 2, px: 4 }}>
                                                                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                                                        <Typography variant="subtitle2">
                                                                            Review History — {fcfo.firstName || fcfo.displayName}
                                                                        </Typography>
                                                                        <Box display="flex" alignItems="center" gap={1}>
                                                                            <Typography variant="caption" color="text.secondary">Certificate format:</Typography>
                                                                            <Select
                                                                                size="small"
                                                                                value={reviewCertExportFormat}
                                                                                onChange={(e) => setReviewCertExportFormat(e.target.value)}
                                                                                sx={{ minWidth: 110, fontSize: '0.75rem' }}
                                                                            >
                                                                                <MenuItem value="pdf">PDF</MenuItem>
                                                                                <MenuItem value="csv">CSV</MenuItem>
                                                                                <MenuItem value="word">Word</MenuItem>
                                                                                <MenuItem value="json">JSON</MenuItem>
                                                                            </Select>
                                                                        </Box>
                                                                    </Box>
                                                                    {fcfo.reviewLogs.length === 0 ? (
                                                                        <Typography variant="body2" color="text.secondary">No review logs found for this advisor.</Typography>
                                                                    ) : (
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell><strong>Period</strong></TableCell>
                                                                                    <TableCell><strong>Assessment</strong></TableCell>
                                                                                    <TableCell align="center"><strong>Events</strong></TableCell>
                                                                                    <TableCell align="center"><strong>Sampled</strong></TableCell>
                                                                                    <TableCell align="center"><strong>Findings</strong></TableCell>
                                                                                    <TableCell><strong>Type</strong></TableCell>
                                                                                    <TableCell><strong>Submitted</strong></TableCell>
                                                                                    <TableCell><strong>Certificate</strong></TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {fcfo.reviewLogs.map(log => {
                                                                                    const periodStartDate = log.periodStart?.toDate?.() || (log.periodStart ? new Date(log.periodStart) : null);
                                                                                    const periodEndDate = log.periodEnd?.toDate?.() || (log.periodEnd ? new Date(log.periodEnd) : null);
                                                                                    const createdDate = log.createdAt?.toDate?.() || (log.createdAt ? new Date(log.createdAt) : null);
                                                                                    const periodLabel = periodStartDate && periodEndDate
                                                                                        ? `${periodStartDate.toLocaleDateString('en-US', { timeZone: 'UTC' })} – ${periodEndDate.toLocaleDateString('en-US', { timeZone: 'UTC' })}`
                                                                                        : 'Unknown';
                                                                                    return (
                                                                                        <TableRow key={log.id}>
                                                                                            <TableCell>{periodLabel}</TableCell>
                                                                                            <TableCell>
                                                                                                <Chip
                                                                                                    label={log.overallAssessment === 'controls_effective' ? 'Controls Effective' : 'Finding Identified'}
                                                                                                    size="small"
                                                                                                    color={log.overallAssessment === 'controls_effective' ? 'success' : 'warning'}
                                                                                                />
                                                                                            </TableCell>
                                                                                            <TableCell align="center">{log.totalEventsInPeriod || 0}</TableCell>
                                                                                            <TableCell align="center">{log.sampleSize || 0}</TableCell>
                                                                                            <TableCell align="center">
                                                                                                {log.findingsCount > 0 ? (
                                                                                                    <Chip label={log.findingsCount} size="small" color="error" />
                                                                                                ) : '0'}
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                <Chip
                                                                                                    label={log.autoGenerated ? 'Auto' : 'Manual'}
                                                                                                    size="small"
                                                                                                    variant="outlined"
                                                                                                    color={log.autoGenerated ? 'default' : 'primary'}
                                                                                                />
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                <Typography variant="body2">
                                                                                                    {createdDate ? createdDate.toLocaleDateString() : '—'}
                                                                                                </Typography>
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                <Button
                                                                                                    size="small"
                                                                                                    variant="outlined"
                                                                                                    disabled={certExportingId === log.id}
                                                                                                    startIcon={certExportingId === log.id ? <CircularProgress size={12} /> : <DownloadIcon />}
                                                                                                    onClick={() => handleExportReviewCertificate(log, reviewCertExportFormat)}
                                                                                                >
                                                                                                    {reviewCertExportFormat.toUpperCase()}
                                                                                                </Button>
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    );
                                                                                })}
                                                                            </TableBody>
                                                                        </Table>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* Incidents Section */}
                            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                                Governance Incidents
                            </Typography>
                            <Paper>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell><strong>Date</strong></TableCell>
                                                <TableCell><strong>Status</strong></TableCell>
                                                <TableCell><strong>Reporter</strong></TableCell>
                                                <TableCell><strong>Data Path</strong></TableCell>
                                                <TableCell><strong>Risk Level</strong></TableCell>
                                                <TableCell><strong>Description</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {complianceData.incidents.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                        <SuccessIcon sx={{ color: 'success.main', fontSize: 32, mb: 1, display: 'block', mx: 'auto' }} />
                                                        <Typography color="text.secondary">No governance incidents reported. All reviews have confirmed controls are effective.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                complianceData.incidents.map(inc => {
                                                    const incDate = inc.createdAt?.toDate?.() || (inc.createdAt ? new Date(inc.createdAt) : null);
                                                    return (
                                                        <TableRow key={inc.id} hover>
                                                            <TableCell>
                                                                <Typography variant="body2">{incDate ? incDate.toLocaleDateString() : '—'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={inc.status === 'reported' ? 'Open' : inc.status === 'resolved' ? 'Resolved' : inc.status}
                                                                    size="small"
                                                                    color={inc.status === 'reported' ? 'error' : inc.status === 'resolved' ? 'success' : 'default'}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{inc.reportedBy || '—'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{formatDataPath(inc.dataPath) || '—'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={inc.riskLevel || 'Unknown'}
                                                                    size="small"
                                                                    color={inc.riskLevel === 'CRITICAL' ? 'error' : inc.riskLevel === 'HIGH' ? 'warning' : 'default'}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {inc.description || '—'}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* Refresh button */}
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchReviewCompliance}
                                    disabled={complianceLoading}
                                >
                                    Refresh Compliance Data
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* ACCESS LOG VIEW */}
            {activeView === 'accesslog' && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Evidence Access Log
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Immutable record of all governance report exports and shares. Required for SOC 2 audit trail integrity.
                    </Typography>

                    <Paper>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Format</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Date Range</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {accessLogsLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <CircularProgress size={24} sx={{ mr: 1 }} />
                                                <Typography component="span" color="text.secondary">
                                                    Loading access logs...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : accessLogs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    No access logs recorded yet. Logs are created when governance reports are exported or shared.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        accessLogs.map((log) => (
                                            <TableRow key={log.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatAccessLogDate(log.timestamp)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={log.action === 'governance_export' ? 'Export' : 'Share'}
                                                        size="small"
                                                        color={log.action === 'governance_export' ? 'primary' : 'secondary'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {log.userEmail || 'Unknown'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {log.action === 'governance_export' ? (
                                                        <Chip
                                                            label={(log.format || 'N/A').toUpperCase()}
                                                            size="small"
                                                            variant="outlined"
                                                            color={
                                                                log.format === 'pdf' ? 'error' :
                                                                log.format === 'csv' ? 'success' :
                                                                log.format === 'json' ? 'warning' :
                                                                'default'
                                                            }
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {log.action === 'governance_share' ? (
                                                        <Typography variant="body2">
                                                            {log.recipientEmail || 'N/A'}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {log.reportType === 'review_certificate'
                                                            ? log.reviewPeriod || '—'
                                                            : log.dateRange?.from && log.dateRange?.to
                                                                ? `${log.dateRange.from} to ${log.dateRange.to}`
                                                                : '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {log.reportType === 'review_certificate'
                                                            ? '—'
                                                            : log.itemCount?.toLocaleString() || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* SOC 2 Compliance Note */}
                    <Paper sx={{ p: 2, mt: 3, bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.main' }}>
                        <Typography variant="body2" color="success.dark">
                            <strong>SOC 2 Compliance:</strong> This log is immutable — records cannot be modified or deleted. 
                            Each entry captures who accessed governance evidence, when, and what data was included. 
                            This provides auditors with verifiable chain of custody for compliance documentation.
                        </Typography>
                    </Paper>
                </Box>
            )}

            {/* Export Modal */}
            <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {activeView === 'accesslog' ? 'Export Evidence Access Log' : 'Export AI Governance Report'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {activeView === 'accesslog'
                            ? `Export the evidence access log (${dateFrom} to ${dateTo}).`
                            : `Export governance data for ${selectedClientName} in the selected date range (${dateFrom} to ${dateTo}).`}
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Export Format</InputLabel>
                        <Select 
                            value={exportFormat} 
                            label="Export Format" 
                            onChange={(e) => setExportFormat(e.target.value)}
                        >
                            <MenuItem value="csv">
                                {activeView === 'accesslog' ? 'CSV (.csv) - Evidence access log, spreadsheet-ready' : 'CSV (.csv) - Audit trail data, spreadsheet-ready'}
                            </MenuItem>
                            <MenuItem value="word">
                                {activeView === 'accesslog' ? 'Word Document (.docx) - Formatted evidence access log' : 'Word Document (.docx) - Formatted compliance report'}
                            </MenuItem>
                            <MenuItem value="json">
                                {activeView === 'accesslog' ? 'JSON (.json) - Machine-readable evidence access log' : 'JSON (.json) - Audit trail data, machine-readable'}
                            </MenuItem>
                            <MenuItem value="pdf">
                                {activeView === 'accesslog' ? 'PDF (.pdf) - Formatted evidence access log' : 'PDF (.pdf) - Formatted compliance report'}
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {activeView === 'accesslog' && exportFormat === 'csv' && 'CSV includes the evidence access log with who generated each entry, format, recipient, date range, and item count.'}
                            {activeView === 'accesslog' && exportFormat === 'word' && 'Word document version of the evidence access log.'}
                            {activeView === 'accesslog' && exportFormat === 'json' && 'Structured JSON version of the evidence access log for GRC platforms.'}
                            {activeView === 'accesslog' && exportFormat === 'pdf' && 'Professional PDF version of the evidence access log.'}
                            {activeView !== 'accesslog' && exportFormat === 'csv' && 'CSV includes complete audit trail with all metadata. Best for data analysis and spreadsheet tools.'}
                            {activeView !== 'accesslog' && exportFormat === 'word' && 'Word document with formatted tables. Best for editing and internal distribution.'}
                            {activeView !== 'accesslog' && exportFormat === 'json' && 'Structured JSON format for GRC platforms (Purview, ServiceNow, Vanta). Includes full audit trail with machine-readable schema.'}
                            {activeView !== 'accesslog' && exportFormat === 'pdf' && 'Professional compliance report with charts and tables. Best for SOC 2 auditors and external stakeholders.'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleExport} 
                        variant="contained" 
                        disabled={isExporting}
                        startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                    >
                        {isExporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Modal */}
            <ShareModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                    item={{ 
                    title: `AI Governance Report — ${selectedClientName} (${dateFrom} to ${dateTo})`, 
                    content: `Summary: ${summaryMetrics.total} AI-generated items for ${selectedClientName}, ${summaryMetrics.primarySuccessRate}% primary success, ${reviewMetrics.overall.reviewRate}% review rate` 
                }}
                itemType="governance_report"
                followUps={[]}
                onShare={handleShare}
                user={user}
            />
        </Box>
    );
};

export default FirmPortalAIGovernance;